// Chicago, "Rain on the Chicago River". An October night of steady rain, about 11 pm, from the Riverwalk on the south
// bank of the river's main branch, between State and Dearborn, looking west upriver toward Wolf Point and the bend.
// What is really there, left to right: the river-front towers of Wacker Drive on the south bank, the curved green
// glass of the tower at the bend, 311 South Wacker with its lit white crown, and then, rising over everything a
// kilometre and a half off, Willis Tower: black, its bundled tubes stepping back at the 50th, 66th and 90th floors,
// two white antennas with red lamps. Right of it the roofs of the Loop's west side (a mid-rise with a rooftop sign,
// the city's billboard), the long block of the Merchandise Mart on the north bank, and Marina City's two "corncob"
// towers standing on the river, nearer, on the right. Across the river runs the double-deck Wells Street bridge, a
// steel bascule with the L on its upper deck and cars on the lower one, and its stone tender house at the near end.
// We stand on the Riverwalk itself: lamps, a hot-dog stand under a striped umbrella, a cafe in a vault of the State
// Street bridge's abutment at the right edge. The river's long reach is folded into a side view as the other scenes
// do (the bridge is drawn side-on, Willis is drawn a little nearer); the order of everything is as it is seen.
// Not in view: the lake and Navy Pier (behind us), the Wrigley Building and the Michigan Avenue bridge (behind us,
// east), the Board of Trade (hidden behind the Wacker towers).
// Back to front: overcast sky with the city's amber glow, three cloud decks and a far flash now and then -> the far
// Loop -> Willis Tower and its antennas -> scud passing its top -> far rain -> the river-front towers, the billboard's
// roof, Marina City -> the bridge (cars on the lower deck behind the truss, the L on top) and its tender house -> the
// river (mirror, ripples, lamp streaks, rain rings, boats, mist) -> mid rain -> the Riverwalk and its people ->
// near rain -> vignette.
// render(t, { crowd }) thins the people on the Riverwalk; crowd 0 draws nobody at all (see CROWD).
//
// The loop's timeline (seconds; every period divides 240; everything else runs all loop long):
//   event                                         time                         sound
//   the L crosses the bridge (east, west ...)     6-18, 66-78, 126-138, 186-198  train at 6, 66, 126, 186
//   the jogger in a hood runs by                  18-40, 98-120, 178-200
//   a customer at the hot-dog stand               every 40 s (2-34, 42-74 ...), served at 18, 58 ...
//   the water taxi comes up the river             28-70                        horn at 29, boat
//   someone with an umbrella walks by             40-86 (right to left), 150-196 (left to right)
//   gulls cross over the river                    50-62, 160-172               gull at 52, 162
//   the billboard's right lamp sputters           47, 183
//   an airliner sinks toward the airport          76-120
//   a far flash in the clouds, thunder            96, 205                      thunder ~1 s later (city weather only)
//   the Riverwalk lamp dips                       112
//   scud veils the top of Willis Tower            110-160
//   the sightseeing boat comes down the river     138-196                      horn at 139, boat
//   the couple under the lamp lean in             every 40 s; she points at each boat
//   the photographer raises a phone               every 20 s; films the L when it crosses
//   the bridge tender sips, looks out             every 30 s; at each boat
//   a car climbs Marina City's parking spiral     every 24 s
//   a cleaning crew lights Willis floor by floor  a floor every 12 s
//   the L's signals turn green before each train  4, 64, 124, 184
// The longest stretch with nothing timed starting or ending is 12 s (220 -> 232), and Willis Tower changes a lit floor every 12 s.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll,
  hash, rng, bayer, hex, makeCanvas, px, rect, makeHalo, drawHalo, text, textWidth, reflect, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const WL = 178;                                      // waterline under the far bank and the bridge
const EMB = 218, CAP = 221, PAVE = 224;              // the Riverwalk's edge stone (top), its face, the paving
const TRK = 146;                                     // the L's rails on the bridge's upper deck
const TOPC = 149, BOTC = 162, GIRD = 169;            // the truss's top chord, the road deck, the under-girder's foot
const BX0 = 44, BX1 = 436, JOINT = 240;              // the bridge's piers (trunnions) and the joint of its two leaves
const WT = { roof: 52, base: 170 };                  // Willis Tower: roof, foot (hidden behind the bridge)
const fy = f => Math.round(WT.base - f * (WT.base - WT.roof) / 108);   // the row of a floor
const WT_COLS = [                                    // its tubes as seen from the east-north-east: two faces
  { x: 186, w: 8, top: fy(90), side: false },        // the tubes that stop at the 90th floor
  { x: 194, w: 8, top: WT.roof, side: false },       // the two that rise to the top
  { x: 202, w: 8, top: WT.roof, side: true },
  { x: 210, w: 6, top: fy(66), side: true },         // the 66th
  { x: 216, w: 4, top: fy(50), side: true },         // the 50th
];
const WT_BANDS = [30, 65, 88, 104].map(fy);          // the dark louvred bands of the mechanical floors
const ANT = [{ x: 197, tip: 22 }, { x: 206, tip: 25 }];   // the two antennas
const CROWN = { x: 152, w: 18, top: 92 };            // 311 South Wacker and its lit crown
const MC = [372, 402], MC_R = 10, MC_TOP = 78, MC_PARK = 124;   // Marina City: tower centres, radius, roof, garage top
const BILL = { x: 234, y: 98, w: 52, h: 20 };        // the rooftop sign's face
const BILL_ROOF = 132;                               // the roof of the mid-rise it stands on
const LAMPS = [{ x: 126, y: 172, base: 236 }, { x: 334, y: 172, base: 236 }];   // Riverwalk lamps (lantern, foot)
const CART = { x: 44, top: 226, base: 248 };         // the hot-dog stand (left edge, counter top, wheels' foot)
const CAFE = { x: 404, ax: 418, aw: 52, top: 176, base: 248 };   // the cafe vault (wall's left edge, arch, foot)
const TH = { x: 8, w: 30, roof: 128, wall: 138, base: 162 };     // the bridge tender house at the near end
const BOLLARDS = [196, 268, 378];
const TREE = { x: 246, cy: 197, rx: 15, ry: 10, planter: 230 };   // a honey locust in a granite planter

/* ================= Colour + pixel helpers ================= */
const RGB = {};
const rgbOf = h => RGB[h] || (RGB[h] = hex(h));
const hx2 = v => (v < 16 ? '0' : '') + v.toString(16);
/** #rrggbb between two #rrggbb colours */
function mixHex(a, b, t) {
  const A = rgbOf(a), B = rgbOf(b);
  return '#' + hx2(Math.round(lerp(A[0], B[0], t))) + hx2(Math.round(lerp(A[1], B[1], t))) + hx2(Math.round(lerp(A[2], B[2], t)));
}
/** ordered-dither pick from a palette (v in 0..1) */
function dpick(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** ImageData-backed painter for prerendering (hex colours) */
function painter(w = W, h = H) {
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  const p = { c, x, w, h };
  p.set = (i, y, col) => {
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
  };
  p.seta = (i, y, col, a) => {
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = Math.round(a * 255);
  };
  p.has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  p.rect = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) p.set(i, y, col); };
  p.line = (x0, y0, x1, y1, col) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), col);
  };
  p.done = () => { x.putImageData(img, 0, 0); return c; };
  return p;
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function flipH(c) {
  const [f, x] = makeCanvas(c.width, c.height);
  x.translate(c.width, 0); x.scale(-1, 1); x.drawImage(c, 0, 0);
  return f;
}
/** crop a full-screen layer to its painted bounding box so each frame blends only the pixels it needs */
function trim(c) {
  const w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) if (d[(y * w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return { c, x: 0, y: 0 };
  const [t, tx] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  tx.drawImage(c, x0, y0, t.width, t.height, 0, 0, t.width, t.height);
  return { c: t, x: x0, y: y0 };
}
const blit = (L, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(L.c, L.x, L.y); ctx.globalAlpha = 1; };
const R = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
const ALPHA = {};                                    // cached rgba() strings
const rgbaC = (h, a) => { const k = h + a; return ALPHA[k] || (ALPHA[k] = rgba2(h, a)); };
function rgba2(h, a) { const [r, g, b] = rgbOf(h); return `rgba(${r},${g},${b},${a})`; }

/* ================= Sky: rain overcast, the Loop's amber glow underneath ================= */
const SKY = ['#090c17', '#0c101d', '#101524', '#141a2c', '#191f33', '#1f253a', '#272b41', '#313147', '#3c374b',
  '#483d4d', '#55444f', '#614a50', '#6e5251'];
function renderSky() {
  const p = painter(W, PAVE);
  for (let y = 0; y < PAVE; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 182), 1.25) * 0.78;
    v += Math.max(0, 1 - Math.hypot((i - 205) / 300, (y - 182) / 120)) * 0.28;      // the Loop's glow under the deck
    v += Math.max(0, 1 - Math.hypot((i - 400) / 160, (y - 182) / 80)) * 0.12;       // River North
    p.set(i, y, dpick(SKY, v, i, y));
  }
  return p.done();
}

/* ================= Low rain clouds (tileable): slate tops, sodium-lit bellies ================= */
const CLOUD_DEFS = [
  { seed: 11, y: -8, h: 58, banks: 7, k: 1, pal: { top: '#262a3c', body: '#181c2a', core: '#141724', bot: '#2c2a38', bot2: '#20212f' } },
  { seed: 29, y: 22, h: 62, banks: 7, k: 2, pal: { top: '#323548', body: '#202436', core: '#1b1f30', bot: '#4a3e48', bot2: '#34303f' } },
  { seed: 43, y: 70, h: 58, banks: 6, k: 3, pal: { top: '#3c3d52', body: '#272a3c', core: '#222536', bot: '#634e52', bot2: '#463c48' } },
];
const SCUD_DEF = { seed: 57, y: 28, h: 46, banks: 9, k: 4, pal: { top: '#3a3b4e', body: '#2c2e40', core: '#272a3a', bot: '#4c4250', bot2: '#3a3646' } };
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 60 + r() * 90, base = h * (0.5 + r() * 0.3);
    const np = 7 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 11 + r() * 19, ry = 4 + r() * 6.5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.35;
      if (v > best) best = v;
    }
    best += 0.16 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = painter(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= The far Loop and River North (blocks, crowns, window dots) ================= */
function renderFar() {
  const p = painter(W, H), r = rng(17), wins = [];
  const addWin = (wx, wy, col) => wins.push({ id: wins.length, x: wx, y: wy, n: 2 + Math.floor(r() * 7), p: 0.25 + r() * 0.35, off: r(), col });
  const block = (x0, w, top, body, edge, pitchY = 3, pitchX = 2, pLit = 0.22) => {
    for (let y = top; y < GIRD; y++) for (let i = x0; i < x0 + w; i++) p.set(i, y, i === x0 ? edge : body);
    for (let i = x0; i < x0 + w; i++) p.set(i, top, edge);
    for (let wy = top + 2; wy < GIRD - 2; wy += pitchY) for (let wx = x0 + 1; wx < x0 + w - 1; wx += pitchX) {
      const v = r();
      if (v < pLit * 0.55) p.set(wx, wy, r() < 0.6 ? '#7a6a54' : '#5e6a80');
      else if (v < pLit) addWin(wx, wy, r() < 0.6 ? '#a88a5e' : '#8496b4');
    }
  };
  // the farthest row: low blocks all along, softened by distance
  let bx = 0;
  while (bx < W) {
    const bw = 8 + Math.floor(r() * 16), top = 120 + Math.floor(r() * 26);
    block(bx, bw, top, r() < 0.5 ? '#1b2132' : '#1e2436', '#283046', 3, 3, 0.18);
    bx += bw;
  }
  // taller Loop towers, left of Willis
  for (const [x0, w, top] of [[4, 16, 96], [22, 12, 108], [38, 18, 88], [60, 14, 102], [78, 12, 94], [96, 16, 84], [118, 12, 104], [134, 14, 98], [174, 10, 110]]) {
    block(x0, w, top, '#171c2b', '#2a3247');
    if (r() < 0.6) { p.rect(x0 + (w >> 1) - 1, top - 4, 3, 4, '#171c2b'); p.set(x0 + (w >> 1), top - 5, '#2a3247'); }
  }
  // right of Willis: the west Loop and River North
  for (const [x0, w, top] of [[224, 12, 104], [238, 16, 116], [300, 14, 100], [318, 10, 110], [420, 18, 92], [440, 14, 104], [456, 24, 86]]) block(x0, w, top, '#171c2b', '#2a3247');
  // the Merchandise Mart: a long, massive block on the north bank, with its corner towers
  for (let y = 118; y < GIRD; y++) for (let i = 292; i < 352; i++) p.set(i, y, (i - 292) % 12 === 0 ? '#262c3e' : '#1d2232');
  for (const tx of [292, 340]) { p.rect(tx, 112, 12, 6, '#1d2232'); p.rect(tx, 112, 12, 1, '#2e364c'); p.rect(tx + 4, 108, 4, 4, '#1d2232'); }
  p.rect(292, 118, 60, 1, '#2e364c');
  for (let wy = 121; wy < GIRD - 2; wy += 3) for (let wx = 293; wx < 351; wx += 2) if (r() < 0.32) addWin(wx, wy, r() < 0.7 ? '#b8925a' : '#8a9ab6');
  return { c: p.done(), wins };
}
/* a blue-grey haze of rain that pushes the far city back */
function renderHaze() {
  const p = painter(W, H);
  for (let y = 70; y < WL; y++) for (let i = 0; i < W; i++) {
    const v = smooth(76, 172, y) * 0.9;
    if (v > bayer(i, y) * 0.95) p.set(i, y, '#4a4e66');
  }
  return p.done();
}

/* ================= Willis Tower: black bundled tubes, lit window grid, two antennas ================= */
const WT_FRONT = ['#151821', '#1a1e29', '#212634'], WT_SIDE = ['#0b0c12', '#0f1118', '#141720'];
function renderWillis() {
  const p = painter(W, H), r = rng(31), wins = [];
  const inBand = y => WT_BANDS.some(b => y >= b - 1 && y <= b + 1);
  WT_COLS.forEach((col, ci) => {
    const pal = col.side ? WT_SIDE : WT_FRONT;
    for (let y = col.top; y < WT.base; y++) for (let i = col.x; i < col.x + col.w; i++) {
      const dx = i - col.x, v = smooth(50, 172, y) * 0.75 + (col.side ? 0 : 0.15);
      let c = (dx & 1) ? dpick(pal, v, i, y) : (col.side ? '#08090e' : '#0e1017');   // black mullion stripes
      if (inBand(y)) c = (i + y) & 1 ? '#07080c' : (col.side ? '#11131a' : '#1a1d27');
      if (y === col.top) c = col.side ? '#2a3042' : '#46506a';                       // the ledge catches the glow
      p.set(i, y, c);
    }
    // the lit window grid: a pane on every glass stripe, every second row, lit floor by floor
    for (let y = col.top + 2; y < WT.base - 1; y += 2) {
      if (inBand(y)) continue;
      const floorBlock = Math.floor((WT.base - y) / 8), base = 0.16 + 0.42 * hash(floorBlock, ci, 7) + (y < 90 ? 0.12 : 0);
      for (let i = col.x + 1; i < col.x + col.w; i += 2) {
        const v = r();
        if (v < base * 0.6) p.set(i, y, col.side ? (r() < 0.5 ? '#6e7484' : '#7e6a4e') : (r() < 0.55 ? '#c4ccd8' : '#d8bc84'));
        else if (v < base) wins.push({ id: wins.length, x: i, y, side: col.side, n: 2 + Math.floor(r() * 6), p: 0.35 + r() * 0.35, off: r(),
          col: col.side ? '#8a8e9a' : (r() < 0.55 ? '#d4dae4' : '#e6c890') });
      }
    }
  });
  // rim light down the west edges, where the city's glow catches the corner
  for (let y = WT_COLS[0].top; y < WT.base; y++) if (bayer(186, y) < 0.7) p.set(186, y, '#323a50');
  for (let y = WT.roof; y < WT_COLS[0].top; y++) p.set(194, y, '#323a50');
  // the Skydeck's glass ledges on the 103rd floor, and its lit floor
  const sk = fy(103);
  for (let i = 194; i < 210; i++) if (i & 1) p.set(i, sk, i < 202 ? '#f0dca8' : '#a89068');
  p.set(193, sk, '#e8e4cc'); p.set(193, sk + 1, '#8a8a90');
  // roof plant between the antennas, then the antennas themselves
  p.rect(199, WT.roof - 2, 6, 2, '#1a1d26'); p.rect(199, WT.roof - 2, 6, 1, '#2e3446');
  for (const a of ANT) {
    const mid = WT.roof - 16;
    for (let y = a.tip; y < WT.roof; y++) {
      if (y >= mid) { p.set(a.x, y, '#b4bccb'); p.set(a.x + 1, y, '#5c6478'); if ((y - mid) % 4 === 0) { p.set(a.x - 1, y, '#7a8296'); p.set(a.x + 2, y, '#4a5164'); } }
      else p.set(a.x, y, y < a.tip + 3 ? '#d8dce6' : '#a4acbc');
    }
    p.rect(a.x - 1, WT.roof - 1, 4, 1, '#3a4256');
  }
  return { c: p.done(), wins };
}

/* ================= River-front towers, the billboard's mid-rise, River North ================= */
const GLASS333 = ['#0c2224', '#123032', '#183e3e', '#22504c', '#30645c', '#467a6e'];
function renderMid() {
  const p = painter(W, H), r = rng(47), wins = [];
  const addWin = (x, y, col) => wins.push({ id: wins.length, x, y, n: 2 + Math.floor(r() * 6), p: 0.3 + r() * 0.35, off: r(), col });
  // 311 South Wacker: granite shaft, its crown a lit white cylinder (glow and ribs drawn per frame)
  const C = CROWN;
  for (let y = C.top + 7; y < GIRD; y++) for (let i = C.x; i < C.x + C.w; i++) {
    let c = i < C.x + 3 ? '#2a2632' : i > C.x + C.w - 4 ? '#15141c' : '#1f1d28';
    if ((i - C.x) % 3 === 0) c = '#141219';
    p.set(i, y, c);
  }
  for (let wy = C.top + 10; wy < GIRD - 2; wy += 3) for (let wx = C.x + 1; wx < C.x + C.w - 1; wx += 3) {
    const v = r(); if (v < 0.2) p.set(wx + 1, wy, '#c8a066'); else if (v < 0.36) addWin(wx + 1, wy, '#d8b070');
  }
  p.rect(C.x - 1, C.top + 6, C.w + 2, 1, '#3a3644');
  for (let y = C.top; y < C.top + 6; y++) for (let i = C.x + 2; i < C.x + C.w - 2; i++) {
    const dx = i - C.x - 2, ww = C.w - 4;
    p.set(i, y, dx % 3 === 1 ? '#9aa4b8' : dx < ww / 2 ? '#e8eef8' : '#c4ccdc');
  }
  for (const tx of [C.x + 1, C.x + C.w - 3]) { p.rect(tx, C.top - 2, 2, 8, '#2a2834'); p.set(tx, C.top - 3, '#4a4656'); }
  p.rect(C.x + 4, C.top - 1, C.w - 8, 1, '#d8e0ee');

  // the domed tower on the corner of Wabash and the river (left edge)
  for (let y = 122; y < GIRD; y++) for (let i = 0; i < 46; i++) {
    let c = (i & 3) === 0 ? '#2e2a2c' : '#3a3434';
    if (y === 122) c = '#5a4e48';
    p.set(i, y, c);
  }
  for (let wy = 126; wy < GIRD - 2; wy += 4) for (let wx = 2; wx < 44; wx += 4) {
    const v = r();
    if (v < 0.3) { p.set(wx, wy, '#e0a85a'); p.set(wx, wy + 1, '#b07e44'); } else if (v < 0.5) addWin(wx, wy, '#e8b468');
  }
  for (let y = 100; y < 122; y++) {                                   // the drum, the dome and its lantern
    const hw = y < 106 ? Math.round(Math.sqrt(Math.max(0, 1 - ((106 - y) / 8) ** 2)) * 9) : 9;
    for (let dx = -hw; dx <= hw; dx++) {
      let c = y < 106 ? (dx < -2 ? '#5e5a48' : dx < 3 ? '#4a4a3c' : '#323226') : (dx < -5 ? '#4a4240' : '#3a3434');
      if (y >= 108 && y < 118 && (dx + 9) % 4 === 1) c = '#d8a458';                 // arched windows of the drum
      p.set(22 + dx, y, c);
    }
  }
  p.rect(20, 94, 5, 4, '#4a4a3c'); p.set(22, 92, '#6a6656'); p.set(22, 93, '#6a6656');

  // the curved green glass tower at the bend: a cylinder of glass mirroring the river's light
  for (let y = 110; y < GIRD; y++) for (let i = 62; i < 124; i++) {
    const s = (i - 62) / 61, v = 0.22 + 0.55 * Math.sin(Math.PI * Math.pow(s, 0.8)) + smooth(130, 168, y) * 0.25;
    let c = dpick(GLASS333, v, i, y);
    if ((y - 110) % 3 === 0) c = '#0a1a1c';
    if (y === 110) c = '#5a8a80';
    if (y < 113 && i > 116) continue;                                 // its clipped corner
    p.set(i, y, c);
  }
  for (let wy = 112; wy < GIRD - 2; wy += 3) for (let wx = 64; wx < 122; wx += 2) if (r() < 0.06) addWin(wx, wy + 1, '#cfe6dc');
  // a low dark block before 311
  for (let y = 128; y < GIRD; y++) for (let i = 124; i < 150; i++) p.set(i, y, y === 128 ? '#3a3440' : '#1e1c26');
  for (let wy = 131; wy < GIRD - 2; wy += 3) for (let wx = 126; wx < 148; wx += 3) if (r() < 0.3) p.set(wx, wy, '#c09060');

  // the brick mid-rise that carries the billboard (its roof, a water tank)
  for (let y = BILL_ROOF; y < GIRD; y++) for (let i = 226; i < 298; i++) {
    let c = ((i >> 1) + (y >> 1)) % 5 === 0 ? '#2a1a1e' : '#341f22';
    if (y === BILL_ROOF) c = '#5a3a34'; else if (y === BILL_ROOF + 1) c = '#24161a';
    p.set(i, y, c);
  }
  for (let wy = BILL_ROOF + 4; wy < GIRD - 2; wy += 4) for (let wx = 229; wx < 296; wx += 4) {
    const v = r();
    if (v < 0.3) { p.rect(wx, wy, 2, 2, '#e0a050'); p.set(wx, wy, '#ffd08a'); } else if (v < 0.5) addWin(wx, wy, '#f0b060');
  }
  p.rect(289, BILL_ROOF - 9, 7, 7, '#3a2a24'); p.rect(288, BILL_ROOF - 10, 9, 1, '#5a4434');
  for (let k = 0; k < 4; k++) p.set(289 + k * 2, BILL_ROOF - 2, '#2a1e1a');
  for (let y = BILL_ROOF - 9; y < BILL_ROOF - 2; y += 2) p.rect(289, y, 7, 1, '#46342c');

  // River North at the right edge
  for (let y = 120; y < GIRD; y++) for (let i = 432; i < W; i++) p.set(i, y, y === 120 ? '#3a3a4c' : (i & 3) === 0 ? '#1a1c28' : '#222434');
  for (let wy = 123; wy < GIRD - 2; wy += 3) for (let wx = 434; wx < W - 1; wx += 3) {
    const v = r(); if (v < 0.25) p.set(wx, wy, r() < 0.5 ? '#d8a868' : '#a8b8d0'); else if (v < 0.4) addWin(wx, wy, '#e0b878');
  }
  return { c: p.done(), wins };
}

/* ================= Marina City: two concrete corncobs of petal balconies over an open spiral garage =================
   Each tower is a cylinder lit from the Loop on its left: dark at both edges, brightest just left of its centre. Every
   flat floor is a ring of rounded petal balconies (a lit rim, the lip of each petal, then the glass set back under it),
   so both outlines scallop floor by floor; the petals of all floors line up into the cob's rows of kernels. A recessed
   service floor sits on the garage, whose open spiral ramps glow sodium-orange with parked cars between their slabs. */
const CONC = ['#2c2c36', '#403f48', '#5a5860', '#77736e', '#958e84', '#b2a998'];
const GARAGE = ['#2a1a12', '#4a2e18', '#6e4620', '#94622a', '#b8823a'];   // the garage's sodium-lit ceilings, dim to bright
const MC_SVC = MC_PARK - 3;                          // the service floor's top row (the flats end above it)
/** the cylinder's light at offset s (-1 left edge .. 1 right edge): dark rims, lit just left of centre */
const mcLight = s => clamp(0.04 + 0.86 * Math.pow(Math.max(0, 1 - s * s), 0.9) - 0.2 * s, 0, 1);
function renderMarina() {
  const p = painter(W, H), r = rng(53), wins = [];
  for (const cx of MC) {
    const lobeAt = dx => Math.cos(Math.asin(clamp(dx / (MC_R + 0.5), -1, 1)) * 8 + cx * 0.7);   // +1 a petal's front, -1 between two
    // the flats: three rows a floor, the outline 9, 10, 8 px from the axis (rounded rim, petal lip, glass set back)
    for (let y = MC_TOP; y < MC_SVC; y++) {
      const f = (y - MC_TOP) % 3, hw = f === 0 ? MC_R - 1 : f === 1 ? MC_R : MC_R - 2;
      for (let dx = -hw; dx <= hw; dx++) {
        const i = cx + dx, s = dx / (MC_R + 0.5), v = mcLight(s), lobe = lobeAt(dx), edge = Math.abs(dx) === hw;
        let c;
        if (f === 0) c = dpick(CONC, Math.max(edge ? 0.3 : 0, v + 0.04 + 0.1 * lobe), i, y);                  // the rim, catching the light
        else if (f === 1) c = lobe > -0.35 || edge ? dpick(CONC, Math.max(edge ? 0.2 : 0, v - 0.08 + 0.08 * lobe), i, y) : '#15151d';   // each petal's lip, dark between petals
        else if (lobe > 0.8 && Math.abs(s) < 0.8) c = dpick(CONC, v * 0.55, i, y);                              // a petal's rounded underside
        else c = Math.abs(s) > 0.7 ? '#0c0d13' : '#11121a';                                                     // the glass in the petal's shadow
        p.set(i, y, c);
      }
      if (f === 2) for (let dx = -MC_R + 3; dx <= MC_R - 3; dx++) {    // windows behind the railings
        if ((dx & 1) || lobeAt(dx) < -0.2 || lobeAt(dx) > 0.8) continue;
        const v = r();
        if (v < 0.14) p.set(cx + dx, y, r() < 0.7 ? '#e8b46a' : '#b8c4d8');
        else if (v < 0.28) wins.push({ id: wins.length, x: cx + dx, y, n: 2 + Math.floor(r() * 6), p: 0.4 + r() * 0.3, off: r(), col: r() < 0.7 ? '#f0bc70' : '#c4d0e4' });
      }
    }
    // the service floor: set back, a thin lit band of plant-room windows
    for (let y = MC_SVC; y < MC_PARK; y++) for (let dx = -(MC_R - 2); dx <= MC_R - 2; dx++) {
      const s = dx / (MC_R + 0.5);
      p.set(cx + dx, y, y === MC_SVC + 1 && !(dx % 3) && Math.abs(s) < 0.7 ? '#7a8a9a' : dpick(CONC, mcLight(s) * 0.45, cx + dx, y));
    }
    // the garage: open spiral ramps, a slab every three rows sloping across the front, sodium light and cars between
    for (let y = MC_PARK; y < GIRD; y++) {
      let car = null;                                  // the car in this three-pixel bay, if any
      for (let dx = -(MC_R - 1); dx <= MC_R - 1; dx++) {
        const i = cx + dx, s = dx / (MC_R + 0.5), v = mcLight(s), rel = ((y - MC_PARK - Math.round(s * 1.5)) % 3 + 3) % 3;
        if ((dx + MC_R + 2) % 3 === 0) car = Math.abs(s) < 0.8 && r() < 0.5 ? (r() < 0.35 ? ['#8a2e2a', '#2e4a6e', '#a8a8ac', '#c8b070'][Math.floor(r() * 4)] : '#1a1214') : null;
        let c;
        if (rel === 0) c = dpick(CONC, Math.max(Math.abs(dx) === MC_R - 1 ? 0.25 : 0, v + 0.05), i, y);   // the ramp's slab edge
        else if (rel === 1) c = dpick(GARAGE, v * 0.95, i, y);                                             // the lit ceiling of the level below
        else c = car && (dx + MC_R + 2) % 3 !== 2 ? car : dpick(GARAGE, v * 0.6, i, y);                    // cars parked at the rim
        p.set(i, y, c);
      }
    }
    // the roof: a parapet and a small plant room with its lamp (drawn per frame)
    for (let dx = -(MC_R - 2); dx <= MC_R - 2; dx++) p.set(cx + dx, MC_TOP - 1, dpick(CONC, mcLight(dx / (MC_R + 0.5)) * 0.8, cx + dx, MC_TOP - 1));
    p.rect(cx - 3, MC_TOP - 4, 6, 3, '#2a2a34'); p.rect(cx - 3, MC_TOP - 4, 6, 1, '#4a4a56');
  }
  return { c: p.done(), wins };
}

/* ================= The billboard: a rooftop scaffold, catwalk, three gooseneck lamps; the own ad ================= */
const BILL_LAMPS = [BILL.x + 8, BILL.x + 26, BILL.x + 44], BILL_LAMP_Y = BILL.y - 5;
function renderBillboard() {
  const p = painter(W, H), x0 = BILL.x, x1 = BILL.x + BILL.w, yb = BILL.y + BILL.h;
  const steel = '#22252e', steelL = '#3c4250';
  for (const lx of [x0 + 3, x0 + 18, x0 + 34, x1 - 4]) { p.rect(lx, yb, 2, BILL_ROOF - yb, steel); p.rect(lx, yb, 1, BILL_ROOF - yb, steelL); }
  for (let k = 0; k < 3; k++) {                                       // cross-bracing between the legs
    const a = [x0 + 4, x0 + 19, x0 + 35][k], b = [x0 + 18, x0 + 34, x1 - 4][k];
    p.line(a, yb + 3, b, BILL_ROOF - 1, steel); p.line(a, BILL_ROOF - 1, b, yb + 3, steel);
  }
  p.rect(x0 - 2, yb + 2, BILL.w + 4, 1, steelL);                     // the catwalk and its rail
  for (let i = x0 - 2; i < x1 + 2; i += 3) p.set(i, yb + 1, steel);
  p.rect(x0 - 2, yb, BILL.w + 4, 1, steel);
  p.rect(x0 - 1, BILL.y - 1, BILL.w + 2, BILL.h + 2, '#14151b');     // the frame
  p.rect(x0 - 1, BILL.y - 1, BILL.w + 2, 1, '#3c4250');
  for (const lx of BILL_LAMPS) {                                      // goosenecks over the top edge
    p.rect(lx, BILL.y - 3, 1, 2, steel); p.rect(lx - 2, BILL_LAMP_Y, 3, 1, steel); p.set(lx - 3, BILL_LAMP_Y + 1, steel);
    p.rect(lx - 5, BILL_LAMP_Y + 1, 3, 1, '#3a3e48'); p.set(lx - 4, BILL_LAMP_Y + 2, '#fff0c8');
  }
  return p.done();
}
/* LOOP PIE, deep dish: a slice in its pan, steam drawn per frame. dim: the right lamp is out */
const SLICE = [
  '...rRrrRrRr...',
  '..crrrrrrrrrc.',
  '..cRrrrRrrrrc.',
  '..cyyyyyyyyyc.',
  '..cyyYyyyYyyc.',
  '..cyyyyyyyyyc.',
  '..ccccccccccc.',
  '.ppppppppppppp',
  '..PPPPPPPPPPP.',
];
const SLICE_KEY = { r: '#d03828', R: '#8a1e1a', c: '#c8843a', y: '#f2cc5a', Y: '#fff0a0', p: '#4a4a54', P: '#26262e' };
function renderPoster(dim) {
  const [c, x] = makeCanvas(BILL.w, BILL.h), w = BILL.w, h = BILL.h;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const v = (dim ? 0.5 : 0) + y / h * 0.8 + (dim ? Math.max(0, (i - 30) / 22) * 0.6 : 0);
    px(x, i, y, v > bayer(i, y) + 0.2 ? '#5e1416' : v > bayer(i, y) - 0.3 ? '#7a1a1c' : '#962226');
  }
  for (let i = 0; i < w; i++) { px(x, i, 0, '#f2e2c4'); px(x, i, h - 1, '#2a0a0c'); }
  for (let i = 1; i < w - 1; i += 2) px(x, i, 1, '#c84a40');          // a checked rim like a pizzeria's cloth
  x.drawImage(sprite(SLICE, SLICE_KEY), 1, 8);
  text(x, 'LOOP PIE', 15, 4, dim ? '#c8bca6' : '#f8ecd2');
  text(x, 'DEEP DISH', 15, 11, dim ? '#c8a038' : '#ffd24a');
  if (dim) { x.globalAlpha = 0.35; x.fillStyle = '#100406'; x.fillRect(0, 0, w, h); x.globalAlpha = 1; }
  return c;
}

/* ================= The Wells Street bridge: double-deck steel bascule, the L on top, cars below ================= */
const STEEL = { hi: '#5e6c84', l: '#46526a', m: '#36404f', d: '#262d3a', k: '#171b24' };
const FAR_LAMPS = [70, 118, 166, 214, 262, 310, 358, 406];             // the far bank's Riverwalk lamps, under the bridge
const PIER_LAMPS = [BX0 - 4, BX0 + 4, BX1 - 4, BX1 + 4];
/* back: the far bank, the lower deck's slab and girders, the piers. Cars run in front of it, behind the truss */
function renderBridgeBack() {
  const p = painter(W, H);
  for (let y = 158; y <= WL; y++) for (let i = 0; i < W; i++) {          // the far bank's wall under the bridge
    let c = ((i + (y >> 2) * 3) % 9 === 0 || y % 4 === 0) ? '#18181f' : '#202029';
    if (y === WL) c = '#101016';
    p.set(i, y, c);
  }
  for (const lx of FAR_LAMPS) { p.rect(lx, 171, 1, 6, '#0c0c12'); p.set(lx - 1, 170, '#3a3430'); p.set(lx + 1, 170, '#3a3430'); }
  for (let i = BX0; i < BX1; i++) {                                    // the lower deck: far railing, slab, deep girder
    const leaf = i < JOINT ? (i - BX0) / (JOINT - BX0) : (BX1 - i) / (BX1 - JOINT);
    const depth = 2 + Math.round(5 * Math.pow(1 - leaf, 1.6));
    if (i % 3 === 0) p.set(i, 156, STEEL.d);
    p.rect(i, 157, 1, 1, STEEL.d);
    p.set(i, BOTC, '#5a6276'); p.set(i, BOTC + 1, STEEL.m); p.set(i, BOTC + 2, STEEL.d);
    for (let y = BOTC + 3; y < BOTC + 3 + depth; y++) p.set(i, y, (i % 6 === 0) ? STEEL.m : STEEL.k);
    p.set(i, BOTC + 3 + depth, '#0e1016');
  }
  for (const [a, b] of [[0, BX0 + 9], [BX1 - 9, W]]) {                // the abutments and the piers that hold the trunnions
    for (let y = 150; y <= WL; y++) for (let i = a; i < b; i++) {
      const pier = (a === 0 ? i >= BX0 - 8 : i < BX1 + 9);
      let c = pier ? (((y - 150) % 5 === 0 || (i + ((y - 150) / 5 | 0) * 4) % 8 === 0) ? '#3a3632' : '#4a453e') : '#25242c';
      if (pier && y < 154) c = y === 150 ? '#7a7062' : '#5a5248';
      if (pier && (i === (a === 0 ? BX0 - 8 : BX1 + 8))) c = '#2a2824';
      p.set(i, y, c);
    }
  }
  for (const lx of PIER_LAMPS) { p.rect(lx, 145, 1, 5, '#18181e'); p.rect(lx - 1, 143, 3, 2, '#3a342c'); p.set(lx, 143, '#fff0c4'); }
  return p.done();
}
/* front: the truss of the two leaves, the upper deck and the L's track, the trestle beyond the river, the signals */
function renderBridgeFront() {
  const p = painter(W, H);
  for (let i = 0; i < W; i++) {                                        // the upper deck girder and its rails, end to end
    p.set(i, TRK - 1, '#9aa2b2'); p.set(i, TRK, (i % 3) ? '#14161c' : '#3a3a40');
    p.set(i, TRK + 1, STEEL.l); p.set(i, TRK + 2, (i % 4 === 0) ? STEEL.hi : STEEL.m); p.set(i, TRK + 3, STEEL.d);
    if (i < BX0 - 8 || i >= BX1 + 9) p.set(i, TRK + 4, STEEL.k);
  }
  for (const cx of [6, 26, 454, 474]) {                                // trestle columns beyond the river
    for (let y = TRK + 4; y < 168; y++) { p.set(cx, y, STEEL.m); p.set(cx + 1, y, STEEL.k); }
    for (let k = 0; k < 8; k++) { p.set(cx + 2 + k, TRK + 5 + k, STEEL.d); p.set(cx - 1 - k, TRK + 5 + k, STEEL.d); }
  }
  for (let i = BX0; i <= BX1; i++) {                                   // chords
    p.set(i, TOPC, STEEL.l); p.set(i, TOPC + 1, STEEL.d);
    p.set(i, BOTC - 2, STEEL.l); p.set(i, BOTC - 1, STEEL.d);
  }
  const verts = [];
  for (let v = BX0; v <= BX1; v += 12) verts.push(v);
  if (!verts.includes(JOINT)) verts.push(JOINT);
  verts.sort((a, b) => a - b);
  verts.forEach((vx, k) => {
    for (let y = TOPC; y < BOTC; y++) { p.set(vx, y, STEEL.hi); p.set(vx + 1, y, STEEL.d); }
    const nx = verts[k + 1];
    if (nx === undefined) return;
    const toJoint = vx < JOINT;                                        // diagonals lean in toward the joint, like a Pratt truss
    if (toJoint) p.line(vx + 2, TOPC + 2, nx - 1, BOTC - 3, STEEL.m);
    else p.line(vx + 2, BOTC - 3, nx - 1, TOPC + 2, STEEL.m);
  });
  for (let y = TOPC - 1; y <= BOTC; y++) p.set(JOINT + 1, y, '#0c0e12');   // the gap where the two leaves meet
  for (const sx of [BX0 + 10, BX1 - 10]) { p.rect(sx, TRK - 7, 1, 6, '#1a1c22'); p.rect(sx - 1, TRK - 9, 3, 3, '#121318'); }
  return p.done();
}

/* ================= The bridge tender house: limestone, a copper roof, a lit window ================= */
function renderTender() {
  const p = painter(W, H), { x, w, roof, wall, base } = TH;
  for (let y = wall; y < base; y++) for (let i = x; i < x + w; i++) {
    let c = (y - wall) % 4 === 0 ? '#6e6658' : '#857c6a';
    if (i === x) c = '#a49a84'; else if (i >= x + w - 2) c = '#5a5446';
    p.set(i, y, c);
  }
  for (let y = roof; y < wall; y++) {                                  // the copper hip roof gone green
    const k = y - roof, x0 = x + 7 - Math.round(k * 0.9), x1 = x + w - 7 + Math.round(k * 0.9);
    for (let i = x0; i < x1; i++) p.set(i, y, i < x0 + 2 ? '#7aa894' : (i + y) % 5 === 0 ? '#3e6a5a' : '#4e806e');
  }
  p.rect(x - 1, wall, w + 2, 1, '#b4aa92'); p.rect(x - 1, wall + 1, w + 2, 1, '#4a4438');
  p.rect(x + 13, roof - 4, 4, 4, '#4e806e'); p.set(x + 14, roof - 5, '#7aa894');
  for (const wx of [x + 4, x + 18]) {                                  // two tall windows, arched; lit inside
    p.rect(wx, wall + 4, 7, 12, '#2a2620');
    p.rect(wx + 1, wall + 5, 5, 10, '#f0b866'); p.rect(wx + 1, wall + 5, 5, 4, '#ffdc98');
    p.rect(wx + 3, wall + 5, 1, 10, '#6a5034'); p.rect(wx + 1, wall + 9, 5, 1, '#6a5034');
    p.set(wx + 1, wall + 5, '#2a2620'); p.set(wx + 5, wall + 5, '#2a2620');
  }
  p.rect(x + 2, base - 1, w - 4, 1, '#3a362e');
  return p.done();
}

/* ================= The L: four stainless cars with lit windows ================= */
const LCAR = 26, LCARS = 4, LTRAIN = LCAR * LCARS + LCARS - 1, LH = 11;
function renderTrain() {
  const p = painter(LTRAIN, LH), r = rng(61);
  for (let k = 0; k < LCARS; k++) {
    const x0 = k * (LCAR + 1);
    for (let i = 0; i < LCAR; i++) {
      const X = x0 + i;
      if (i > 0 && i < LCAR - 1) p.set(X, 0, '#6e7686');
      p.set(X, 1, '#a8b0bc'); p.set(X, 2, '#c4cad4');
      for (let y = 3; y <= 5; y++) p.set(X, y, '#c4cad4');
      p.set(X, 6, '#b2b8c4'); p.set(X, 7, '#3a5aa0'); p.set(X, 8, '#959ba8');
      p.set(X, 9, '#2a2c34');
    }
    for (let wx = x0 + 2; wx < x0 + LCAR - 3; wx += 4) {               // windows; a door in the middle
      if (Math.abs(wx - (x0 + 11)) < 2) { p.rect(wx, 3, 2, 5, '#7a808c'); p.rect(wx + 1, 3, 1, 5, '#5a606c'); continue; }
      p.rect(wx, 3, 3, 3, '#ffe2a6'); p.rect(wx, 3, 3, 1, '#fff2d0');
      if (r() < 0.5) { p.set(wx + Math.floor(r() * 3), 4, '#3a2e2a'); p.set(wx + Math.floor(r() * 3), 5, '#3a2e2a'); }
    }
    for (const tx of [x0 + 3, x0 + LCAR - 7]) { p.rect(tx, 9, 5, 1, '#1a1c22'); p.set(tx, 10, '#121318'); p.set(tx + 4, 10, '#121318'); }
    p.set(x0, 2, '#7a808c'); p.set(x0 + LCAR - 1, 2, '#7a808c');
  }
  const f = LTRAIN - 1;                                                // the lead cab (facing right)
  p.rect(f - 1, 3, 2, 3, '#1c2030'); p.set(f, 1, '#ffb040'); p.set(f - 1, 1, '#ffb040');
  p.set(f, 8, '#fffbe8'); p.set(0, 8, '#ff4a3a');
  return p.done();
}

/* ================= Cars on the lower deck, boats on the river ================= */
const CAR_ROWS = ['...bbbbb...', '..bwwbwwb..', 'Tbbbbbbbbbh', 'bbbbbbbbbbb', '.kk.....kk.'];
const CAR_COLS = ['#8a2a2a', '#2a3a5a', '#9a9aa2', '#1e1e24', '#e0b020', '#4a5a4a'];
function renderCars() {
  return CAR_COLS.map(col => {
    const key = { b: col, w: '#9ab0c8', T: '#ff3a2a', h: '#fff4d8', k: '#0e0e12' };
    const c = sprite(CAR_ROWS, key);
    if (col === '#e0b020') { const x = c.getContext('2d'); x.fillStyle = '#fff0a0'; x.fillRect(5, 0, 1, 1); }   // a cab's roof light
    return [c, flipH(c)];
  });
}
/* the water taxi: yellow hull, black band, a glazed cabin */
function renderTaxi() {
  const p = painter(38, 14);
  for (let i = 2; i < 38; i++) { const bow = i > 33 ? i - 33 : 0; for (let y = 9 + (bow > 2 ? 1 : 0); y < 14 - bow; y++) p.set(i, y, y === 11 ? '#141416' : y < 11 ? '#e8bc30' : '#b88a20'); }
  for (let i = 0; i < 3; i++) p.set(i + 1, 10, '#b88a20');
  p.rect(8, 3, 22, 6, '#e8e2d4'); p.rect(8, 3, 22, 1, '#ffffff'); p.rect(7, 2, 24, 1, '#3a3a40');
  for (let wx = 10; wx < 28; wx += 4) { p.rect(wx, 5, 3, 2, '#ffd890'); p.set(wx, 5, '#fff2c8'); }
  p.rect(14, 0, 1, 2, '#5a5a62'); p.set(14, 0, '#ffffff');
  return p.done();
}
/* the sightseeing boat: long and low, a canopy over the open deck, the lounge lit below */
function renderTour() {
  const p = painter(72, 16), r = rng(67);
  for (let i = 0; i < 72; i++) {
    const bow = i < 6 ? 6 - i : 0;
    for (let y = 10 + (bow >> 1); y < 16 - (bow >> 2); y++) p.set(i, y, y === 10 + (bow >> 1) ? '#e8eaf0' : y < 13 ? '#20304e' : '#141e34');
  }
  for (let wx = 8; wx < 68; wx += 3) { p.rect(wx, 11, 2, 1, '#ffd88a'); }
  p.rect(10, 9, 60, 1, '#c8ccd8');                                      // the open deck's rail
  for (let i = 10; i < 70; i += 3) p.rect(i, 6, 1, 3, '#8a90a0');
  p.rect(26, 2, 44, 2, '#eef0f4'); p.rect(26, 4, 44, 1, '#9aa0b0');      // the canopy
  for (const cx of [27, 47, 68]) p.rect(cx, 4, 1, 5, '#6a7080');
  for (let i = 12; i < 68; i += 4) if (r() < 0.65) {                      // passengers under the canopy and in the rain
    const hx = i + Math.floor(r() * 2), under = hx > 27;
    p.rect(hx, 6, 2, 3, ['#3a4a6a', '#6a3a3a', '#4a4a3a', '#2a2a34'][Math.floor(r() * 4)]);
    p.set(hx, 5, '#c8a080');
    if (!under) { p.rect(hx - 2, 3, 6, 1, ['#2a6ab0', '#c83a3a', '#2a2a30'][Math.floor(r() * 3)]); p.set(hx + 1, 4, '#1a1a1e'); }
  }
  p.rect(66, 0, 1, 2, '#8a90a0'); p.set(66, 0, '#ffffff');
  return p.done();
}

/* ================= The river: deep green-black water (the mirror and ripples go on per frame) ================= */
function renderWater() {
  return ditherFill(W, EMB - WL, ['#0d1418', '#0f171d', '#121b22', '#151f28'], (i, y) => 0.15 + 0.6 * Math.max(0, 1 - Math.hypot((i - 220) / 260, y / 30)));
}
function ditherFill(w, h, pal, fn) {
  const p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) p.set(i, y, dpick(pal, fn(i, y), i, y));
  return p.done();
}

/* ================= The Riverwalk: granite edge, wet paving, bollards ================= */
function renderGround() {
  const p = painter(W, H), r = rng(71);
  for (let i = 0; i < W; i++) {
    p.set(i, EMB, '#8a8892'); p.set(i, EMB + 1, '#6a6872'); p.set(i, EMB + 2, (i % 14 === 0) ? '#3a3842' : '#57555f');
    p.set(i, CAP, '#2a2932'); p.set(i, CAP + 1, '#1c1b22'); p.set(i, CAP + 2, '#24232b');
  }
  const rows = [PAVE, 229, 235, 242, 250, 259, H];                     // slab courses, deeper as they come near
  for (let k = 0; k < rows.length - 1; k++) {
    const y0 = rows[k], y1 = rows[k + 1], sw = 16 + k * 6, off = (k * 11) % sw;
    for (let y = y0; y < y1; y++) for (let i = 0; i < W; i++) {
      const slab = Math.floor((i + off) / sw), tone = hash(slab, k, 3);
      let c = tone < 0.33 ? '#1d1c25' : tone < 0.66 ? '#22212b' : '#26252f';
      if (y === y0 || (i + off) % sw === 0) c = '#121118';
      else if (bayer(i, y) < 0.08 + 0.12 * hash(slab, k, 4)) c = '#34333f';   // the wet sheen
      p.set(i, y, c);
    }
  }
  for (let k = 0; k < 40; k++) {                                       // honey-locust leaves stuck to the wet stone
    const x = Math.floor(r() * W), y = PAVE + 2 + Math.floor(r() * (H - PAVE - 4));
    p.set(x, y, r() < 0.5 ? '#c8a030' : '#8a6a24'); if (r() < 0.4) p.set(x + 1, y, '#a8862c');
  }
  for (const bx of BOLLARDS) {                                         // iron mooring bollards on the edge
    p.rect(bx - 1, EMB - 5, 4, 6, '#1a1a20'); p.rect(bx - 2, EMB - 6, 6, 2, '#2c2c34'); p.rect(bx - 2, EMB - 6, 6, 1, '#6a6a76');
    p.set(bx - 1, EMB - 3, '#3a3a44');
  }
  return p.done();
}
/* the Riverwalk's tall black lamps */
function renderLamps() {
  const p = painter(W, H);
  for (const L of LAMPS) {
    const x = L.x;
    p.rect(x - 2, L.base - 6, 5, 6, '#121216'); p.rect(x - 2, L.base - 6, 1, 6, '#34343e'); p.rect(x - 3, L.base - 1, 7, 1, '#0c0c10');
    for (let y = L.y + 6; y < L.base - 6; y++) { p.set(x, y, '#3a3a46'); p.set(x + 1, y, '#121216'); }
    p.rect(x - 2, L.y - 4, 6, 2, '#141418'); p.rect(x - 1, L.y - 5, 4, 1, '#26262e');
    p.rect(x - 2, L.y - 2, 6, 7, '#ffe2a8'); p.rect(x - 1, L.y - 2, 4, 3, '#fff6dc');
    for (const dx of [-2, 3]) p.rect(x + dx, L.y - 2, 1, 7, '#2a2a32');
    p.rect(x - 2, L.y + 5, 6, 1, '#141418');
  }
  return p.done();
}
/* the honey locust: fine yellow leaves thinning for the autumn, its planter (the crown is drawn per frame, swaying) */
const LEAFY = ['#3a3418', '#5a4e1e', '#8a7424', '#b8962c', '#dcb840'];
function renderTreeCrown() {
  const { rx, ry } = TREE, w = rx * 2 + 7, h = ry * 2 + 5, p = painter(w, h), r = rng(77);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const dx = (i - w / 2) / rx, dy = (y - h / 2) / ry;
    let v = 1 - dx * dx - dy * dy + (bayer(i, y) - 0.5) * 0.5 + (r() - 0.5) * 0.35;
    if (v <= 0 || r() < 0.18) continue;
    const light = 0.35 + 0.35 * (dy > 0.2 ? 1 : 0) * (1 - Math.abs(dx)) - dx * 0.15 + (r() - 0.5) * 0.3;   // the lamps light its underside
    p.set(i, y, dpick(LEAFY, light + (v < 0.3 ? -0.25 : 0), i, y));
  }
  return p.done();
}
function renderTree() {
  const p = painter(W, H), { x, planter } = TREE;
  for (let y = TREE.cy; y < planter; y++) { p.set(x, y, '#2a2018'); p.set(x + 1, y, '#16120e'); }
  p.line(x, TREE.cy + 6, x - 6, TREE.cy + 1, '#2a2018'); p.line(x + 1, TREE.cy + 4, x + 7, TREE.cy - 1, '#2a2018');
  p.rect(x - 11, planter, 24, 10, '#4a4852'); p.rect(x - 11, planter, 24, 1, '#7a7882'); p.rect(x - 11, planter + 1, 24, 1, '#2a2830');
  p.rect(x + 9, planter + 2, 3, 8, '#38363e'); p.rect(x - 11, planter + 9, 24, 1, '#1e1c22');
  return p.done();
}
/* the hot-dog stand: striped umbrella, a steamer, a cream cart with HOT DOGS on its front */
function renderCart() {
  const p = painter(W, H), x0 = CART.x, top = CART.top, cx = x0 + 24;
  [[0, 9], [1, 15], [2, 19], [3, 22], [4, 24], [5, 25], [6, 26], [7, 26]].forEach(([dy, hw]) => {
    for (let dx = -hw; dx <= hw; dx++) {
      const seg = Math.floor((dx + 30) / 6) & 1;
      p.set(cx + dx, 196 + dy, seg ? (dy < 2 ? '#f0cc58' : '#dcb040') : (dy < 2 ? '#d84a3a' : '#b83428'));
    }
  });
  for (let dx = -26; dx <= 26; dx++) if ((dx + 30) % 6 < 4) p.set(cx + dx, 204, ((Math.floor((dx + 30) / 6)) & 1) ? '#b8902e' : '#8e2820');
  p.rect(cx, 205, 1, top - 205, '#8a8a92');
  p.rect(x0 + 2, top, 44, 2, '#b4bac4'); p.rect(x0 + 2, top, 44, 1, '#dce0e8');    // the counter
  p.rect(x0 + 6, top - 5, 14, 5, '#8a909a'); p.rect(x0 + 6, top - 5, 14, 1, '#c8ccd4'); p.rect(x0 + 7, top - 3, 12, 1, '#5a606a');
  p.rect(x0 + 36, top - 5, 2, 5, '#d83a2a'); p.rect(x0 + 39, top - 5, 2, 5, '#e8c030'); p.set(x0 + 36, top - 6, '#ffffff'); p.set(x0 + 39, top - 6, '#4a4a4a');
  for (let y = top + 2; y < top + 15; y++) for (let i = x0 + 2; i < x0 + 46; i++) {
    let c = y < top + 11 ? '#e2dac8' : '#bab09c';
    if (y === top + 2 || y === top + 10) c = '#b8322a';
    if (i === x0 + 2 || i === x0 + 45) c = '#8a8272';
    p.set(i, y, c);
  }
  const done = p.done(), x = done.getContext('2d');
  text(x, 'HOT DOGS', x0 + 24 - (textWidth('HOT DOGS') >> 1), top + 4, '#c02a22');
  const wheel = (wx) => { for (let y = -4; y <= 4; y++) for (let i = -4; i <= 4; i++) { const d = Math.hypot(i, y); if (d < 4.3) px(x, wx + i, top + 19 + y, d > 3 ? '#121216' : d < 1 ? '#8a8a92' : (i === y || i === -y) ? '#5a5a64' : '#26262c'); } };
  wheel(x0 + 9); wheel(x0 + 39);
  return done;
}
/* the cafe in a vault of the State Street bridge's abutment: a girder overhead, stone, an arched window */
function renderCafe() {
  const p = painter(W, H), { x, ax, aw, top, base } = CAFE, r = rng(73), acx = ax + aw / 2, ar = aw / 2, spring = top + ar;
  for (let y = 150; y < 162; y++) for (let i = x - 4; i < W; i++) {   // the bridge girder over the walk
    let c = y === 150 ? '#4a5a5a' : y === 161 ? '#0c1010' : (i % 8 === 0 && (y === 152 || y === 159)) ? '#5a6a68' : (y < 154 ? '#2a3636' : '#1c2626');
    p.set(i, y, c);
  }
  for (let y = 162; y < base; y++) for (let i = x; i < W; i++) {
    const course = Math.floor((y - 162) / 6), off = course & 1 ? 7 : 0;
    let c = ((i + off) % 14 === 0 || (y - 162) % 6 === 0) ? '#25222a' : (hash(Math.floor((i + off) / 14), course, 9) < 0.5 ? '#3a3540' : '#423c46');
    if (i === x) c = '#5a5460';
    p.set(i, y, c);
  }
  for (let y = top; y < base; y++) for (let i = ax; i < ax + aw; i++) {   // the arch: a warm room behind glass
    const dx = i + 0.5 - acx, inArch = y >= spring || Math.hypot(dx, y + 0.5 - spring) < ar;
    if (!inArch) continue;
    const ring = y < spring ? Math.hypot(dx, y + 0.5 - spring) > ar - 2 : (i < ax + 2 || i >= ax + aw - 2);
    let c = ring ? '#6a6070' : y < top + 20 ? '#7a4a2a' : '#5a3420';
    if (!ring && y > base - 20 && y < base - 12) c = y === base - 20 ? '#a8784a' : '#6a3e22';   // the counter
    if (!ring && y >= top + 22 && y < base - 22 && (y - top) % 7 === 0) c = '#3a2216';           // shelves
    p.set(i, y, c);
  }
  for (let k = 0; k < 40; k++) {                                       // bottles on the shelves
    const i = ax + 4 + Math.floor(r() * (aw - 8)), s = top + 22 + Math.floor(r() * 3) * 7;
    if (s < base - 22) p.rect(i, s - 3, 1, 3, ['#4a8a5a', '#c89040', '#8a2a2a', '#d8d0b0'][Math.floor(r() * 4)]);
  }
  for (let i = ax + 2; i < ax + aw - 2; i++) if ((i - ax) % 13 === 0) for (let y = top + 2; y < base; y++) { const dx = i + 0.5 - acx; if (y >= spring || Math.hypot(dx, y - spring) < ar - 2) p.set(i, y, '#1e1612'); }
  for (let i = ax + 2; i < ax + aw - 2; i++) p.set(i, spring, '#1e1612');
  for (let i = x + 1; i < W; i++) p.set(i, base - 1, '#1a181e');
  const sign = p.done(), sx = sign.getContext('2d');
  text(sx, 'CAFE', Math.round(acx - textWidth('CAFE') / 2), 166, '#ffd890');
  // a bistro table and two chairs out on the wet stone, a planter of grasses by the wall
  const q = sx;
  rect(q, 386, 234, 9, 1, '#5a5a66'); rect(q, 386, 235, 9, 1, '#2a2a32'); rect(q, 390, 236, 1, 10, '#2a2a32'); rect(q, 388, 246, 5, 1, '#1a1a20');
  for (const [back, d] of [[380, 1], [399, -1]]) {                    // back post, seat toward the table, front leg
    rect(q, back, 230, 1, 16, '#2a2a32'); rect(q, d > 0 ? back : back - 3, 239, 4, 1, '#3a3a44'); rect(q, back + 3 * d, 240, 1, 6, '#2a2a32');
  }
  rect(q, 404, 238, 14, 10, '#3a3036'); rect(q, 404, 238, 14, 1, '#5a5058');
  for (let k = 0; k < 14; k++) { const gx = 405 + k, h = 4 + ((k * 7) % 6); rect(q, gx, 238 - h, 1, h, k & 1 ? '#6a7a3a' : '#a89a4a'); }
  return sign;
}

/* ================= Gulls, mist, vignette ================= */
const GULL_KEY = { w: '#e6eaf2', g: '#8a96a8', k: '#2a2e38', y: '#e8b830', s: '#c4cad4' };
const GULL_ROWS = [['k.............k', '.g...........g.', '.gw.........wg.', '..gw.......wg..', '...ww.....ww...', '....wwwwwwwwwy.', '......ssss.....'],
  ['...............', 'kgg.........ggk', '..ggww...wwgg..', '....wwwwwwwwwy.', '......ssss.....'],
  ['....wwwwwwwwwy.', '...ww.ssss.ww..', '..gw.......wg..', '.gg.........gg.', 'k.............k']];
const GULL_SIT = [['..ww.', '.wwwy', 'gwww.', 'ggww.', '..s..'], ['.ww..', 'ywww.', '.wwwg', '.wwgg', '..s..']];
const FOG_DEF = { seed: 9, y: 140, h: 64, k: 2, top: '#4e5670', bot: '#6a6a80', alpha: 0.42,
  dens: t => 0.5 + 0.22 * wave(t, 3, 0.15) + 0.08 * wave(t, 8, 0.6) };
const FOG_LEVELS = 8;
function makeFog(def) {
  const h = def.h, n = W * h, F = new Float32Array(n), A = hex(def.top), B = hex(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.6);
    const nz = 0.48 + 0.34 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.05) + 0.16 * Math.sin(TAU * 5 * x / W - y * 0.19 + def.seed * 2)
      + 0.1 * Math.sin(TAU * 11 * x / W + y * 0.41);
    F[y * W + x] = prof * clamp(nz, 0, 1);
  }
  const levels = [];
  for (let k = 0; k <= FOG_LEVELS; k++) {
    const D = k / FOG_LEVELS * 1.2;
    const [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data;
    for (let q = 0; q < n; q++) {
      const x = q % W, y = (q / W) | 0, s = Math.floor(Math.min(1, F[q] * D) * 4 + bayer(x, y)) / 4;
      if (s <= 0) continue;
      const m = y / (h - 1), o = q * 4;
      d[o] = A[0] + (B[0] - A[0]) * m; d[o + 1] = A[1] + (B[1] - A[1]) * m; d[o + 2] = A[2] + (B[2] - A[2]) * m;
      d[o + 3] = Math.round(Math.min(1, s) * 255);
    }
    cx.putImageData(img, 0, 0);
    levels.push(c);
  }
  return { ...def, levels };
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 6; d[o + 1] = 8; d[o + 2] = 14; d[o + 3] = Math.round(q * 0.45 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= People: a few, in rain gear, lit by the lamps and the city ================= */
const NIGHT = '#0a0e18', WARMC = '#ffd49a', COOLC = '#9aaccc';
const SKINS = ['#f0c8a0', '#dca880', '#b88458', '#8a5a3c'];
/** a look: height in px, coat and trousers; o: skin 0..3, hood (true: up), hair, hat, strip (a reflective band),
    apron, dk (how far into the night), warm (lit by a lamp rather than the city) */
function look(h, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  return { h, hh: h >= 22 ? 5 : 4, hw: h >= 19 ? 4 : 3, th: Math.round(h * 0.4), tw: h >= 24 ? 7 : h >= 19 ? 6 : 5,
    skin: m(SKINS[o.skin ?? 0]), top: m(top), topD: mixHex(top, NIGHT, dk + 0.28), topL: mixHex(top, o.warm ? WARMC : COOLC, 0.42),
    bot: m(bot), shoe: m('#16161c'), hood: !!o.hood, hair: m(o.hair || '#2a1c16'), hat: o.hat ? m(o.hat) : null,
    strip: o.strip || null, apron: o.apron ? m(o.apron) : null, long: !!o.long };
}
let PH = { hy: 0, ty: 0, tx: 0, bw: 0 };              // the last person drawn: head top, shoulder row, torso's left column and width
/**
 * One person, feet on row fy, centred on column x.
 * o.view: 0 from behind (looking at the river), 1 facing us, 2 in profile (o.dir: 1 right, -1 left)
 * o.step: stride frame (profile), o.lean: head px towards a neighbour
 * o.aL / o.aR: 0 down, 1 up (holding an umbrella high), 2 bent up to the face (a phone, a cup, a bite), 3 out
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = P.h - hh - th, ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  const ly = fy - legs + 1;
  if (side) {
    if (o.step) {
      R(x - 2, ly, 4, 2, P.bot);
      R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
      R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
    } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
  } else {
    const lw = bw >= 7 ? 3 : 2;
    R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
    R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
  }
  R(tx, ty, bw, th + (P.long ? 4 : 1), P.top);                                        // the coat hangs over the hips
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1 + (P.long ? 3 : 0), P.topD);
  R(tx, ty, bw, 1, P.topL);                                                           // wet shoulders catch the light
  if (P.strip) R(tx, ty + 3, bw, 1, P.strip);
  if (P.apron && view === 1) R(tx + 1, ty + 2, bw - 2, th, P.apron);
  if (side) {
    const st = o.aR || 0;
    if (st === 0) R(x + (o.step ? -dir : 0), ty + 1, 1, th - 1, P.topD);
    else if (st === 1) for (let k = 0; k < 4; k++) R(x + dir * (1 + (k >> 1)), ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin);
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 2 - k, 1, 1, P.topD); R(x + dir * 3, ty - 1, 1, 1, P.skin); }
    else { R(dir > 0 ? x + 1 : x - 4, ty + 2, 4, 1, P.topD); R(x + dir * 5, ty + 2, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) R(ax, ty + 1, 1, th - 1, P.topD);
      else if (st === 1) { R(ax, hy, 1, ty - hy + 2, P.topD); R(ax, hy - 1, 1, 1, P.skin); }
      else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); }
      else { R(sg < 0 ? ax - 3 : ax, ty + 1, 4, 1, P.topD); R(sg < 0 ? ax - 4 : ax + 4, ty + 1, 1, 1, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0);
  if (P.hood) {                                                                       // hood up: the coat's colour, a face inside it
    R(hx, hy, hw, hh, P.top); R(hx, hy, hw, 1, P.topL);
    if (view === 0) R(hx, hy + hh - 1, hw, 1, P.topD);
    else if (view === 1) R(hx + 1, hy + 1, hw - 2, hh - 2, P.skin);
    else R(dir > 0 ? hx + hw - 2 : hx, hy + 1, 2, hh - 2, P.skin);
  } else {
    const cap = P.hat || P.hair;
    if (view === 0) { R(hx, hy, hw, hh, cap); R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin); }
    else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, P.hat ? 2 : 1, cap); if (side) R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1, cap); }
  }
  PH = { hy, ty, tx, bw };
  return hy;
}
/** an open umbrella: canopy centred on cx with its top at row y, half-width hw; the shaft down to the hand at row hand */
function umbrella(cx, y, hw, col, edge, hand) {
  cx = Math.round(cx); y = Math.round(y);
  R(cx, y + 3, 1, Math.max(1, hand - y - 3), '#121216');
  [[0, 0.35], [1, 0.72], [2, 0.92], [3, 1]].forEach(([dy, f]) => { const w = Math.round(hw * f); R(cx - w, y + dy, w * 2 + 1, 1, dy === 0 ? edge : col); });
  R(cx - Math.round(hw * 0.5), y, Math.round(hw * 0.5), 1, edge);
  for (const k of [-hw, -Math.round(hw / 2), Math.round(hw / 2), hw]) px(ctx, cx + k, y + 4, '#141418');
  px(ctx, cx, y - 1, '#141418');
}
const phoneBack = (x, y) => { R(x, y, 2, 3, '#0e1018'); R(x, y, 2, 1, '#ffe2a8'); R(x, y + 1, 2, 1, '#8ad8ff'); };

/* ================= The crowd ================= */
// The share of the people the scene shows by itself. render(t, { crowd }) overrides it; rank 0 (the hot-dog vendor)
// stays while anybody does; crowd 0 draws nobody and leaves the stand unattended, the tender's window empty.
const CROWD = 0.85;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const RANK = { vendor: 0, couple: 0.15, customer: 0.25, photo: 0.3, tender: 0.35, walkA: 0.45, walkB: 0.55, jog: 0.6, cafe: 0.7, tourists: 0.9 };
const COAT = { trench: '#9a8460', navy: '#26324e', red: '#b83a32', charcoal: '#3a3c44', olive: '#5a6038', camel: '#b08a5a',
  plum: '#6a3a5a', teal: '#2a6a72', grey: '#7a7e88', blue: '#2a5a9a', mustard: '#c89a30', black: '#1e1e24' };
const DARK = '#1e2028', DENIM = '#2a3650', KHAKI = '#5a5444';
const CAST = {
  vendor: look(21, '#2c3a5a', DARK, { skin: 2, hat: '#c83a32', apron: '#f4f0e6', warm: true, dk: 0.12 }),
  him: look(22, COAT.navy, DARK, { skin: 0, hair: '#3a2a1e', warm: true, dk: 0.24 }), her: look(20, COAT.trench, DENIM, { skin: 1, hair: '#6a3a1e', long: true, warm: true, dk: 0.24 }),
  photo: look(20, COAT.olive, DARK, { skin: 3, hat: '#2a2a32' }),
  tourA: look(20, COAT.red, DENIM, { skin: 0, hair: '#c8a060' }), tourB: look(21, COAT.teal, KHAKI, { skin: 1, hat: '#d8d0c0' }),
  walkA: look(24, COAT.charcoal, DARK, { skin: 1, hair: '#1a1410', long: true, dk: 0.34 }),
  walkB: look(25, COAT.camel, DENIM, { skin: 0, hair: '#8a5a2a', dk: 0.34 }),
  jog: look(25, COAT.grey, DARK, { skin: 2, hood: true, strip: '#e8f0ff', dk: 0.36 }),
  cafe: look(22, COAT.black, DARK, { skin: 1, hair: '#2a1c16', apron: '#6a3e22', warm: true, dk: 0.2 }),
  customers: [look(23, COAT.plum, DENIM, { skin: 0, hat: '#3a3a52', dk: 0.3 }), look(24, COAT.blue, KHAKI, { skin: 3, hood: true, dk: 0.3 }),
    look(23, COAT.mustard, DARK, { skin: 1, hair: '#2a1c16', dk: 0.3 })],
};
const UMB = { couple: ['#7a2238', '#d06a78'], photo: ['#2a5a8a', '#6a9ac8'], walkA: ['#8e1e2a', '#d8606a'], walkB: ['#c8a020', '#f8dc70'],
  tourists: ['#3a3a44', '#7a7a8a'] };

/* ================= The loop's timeline (seconds; see the table at the top) ================= */
const TRAINS = [{ a: 6, b: 18, dir: 1 }, { a: 66, b: 78, dir: -1 }, { a: 126, b: 138, dir: 1 }, { a: 186, b: 198, dir: -1 }];
const TAXI = { a: 28, b: 70, horn: 29 }, TOUR = { a: 138, b: 196, horn: 139 };
const GULLS_T = [{ a: 50, b: 62, dir: 1, y0: 98, dip: 16 }, { a: 160, b: 172, dir: -1, y0: 86, dip: 14 }];
const PLANE = { a: 76, b: 120 };
const FLASHES = [{ t: 96, x: 404 }, { t: 205, x: 92 }];               // far flashes in the clouds; thunder ~1.2 s later
const LAMP_FLICK = [[112, 113.4]], BILL_FLICK = [[47.4, 49.2], [183.6, 184.6]];
const VEIL = { a: 110, b: 160, f: 16 };                             // the scud thickens over Willis Tower's top
const WALK_A = [40, 86], WALK_B = [150, 196], JOG = [[18, 40], [98, 120], [178, 200]];
const SERVE = { every: 40, from: 2 };                               // a customer at the stand every 40 s
const walkF = t => Math.floor(t * 3.2) & 1;                         // stride frame (3.2 a second: a whole number per loop)
const inWin = (t, list) => { for (const w of list) if (t >= w[0] && t < w[1]) return (t - w[0]) / (w[1] - w[0]); return -1; };
const boatNear = t => (t >= TAXI.a + 8 && t < TAXI.b - 8) || (t >= TOUR.a + 8 && t < TOUR.b - 10);
const trainOn = t => TRAINS.some(tr => t >= tr.a && t < tr.b);
function flick(t, list, id) {
  let v = 1;
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); v = hv > 0.45 ? 1 : hv > 0.2 ? 0.4 : 0.1; }
  return v;
}

/* ================= Rain (3 depths), splashes and rings ================= */
const RAIN_LIGHTS = [
  ...LAMPS.map(L => ({ x: L.x, y: L.y + 2, r: 46 })), { x: CART.x + 24, y: 208, r: 26 }, { x: CAFE.ax + 26, y: 206, r: 36 },
  { x: TH.x + 15, y: TH.wall + 9, r: 18 }, ...PIER_LAMPS.map(x => ({ x, y: 143, r: 9 })),
].map(l => ({ ...l, r2: l.r * l.r }));
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BILL.x + 26, y: BILL.y + 6, r: 32, r2: 32 * 32 }];
const WEATHER = weatherPlans(['rain']);
const WX_LAND = [[150, WL], [WL + 2, EMB - 1], [H + 4, H + 4]];
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
const WX_BB = { land: WX_LAND, lights: RAIN_LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
function litAt(x, y, lights) {
  for (const l of lights) { const dx = x - l.x, dy = y - l.y; if (dx * dx + dy * dy < l.r2) return true; }
  return false;
}
function nearLand(x, a, b) {
  if (x >= CAFE.x - 4) return 150;                                   // the girder shelters the cafe
  if (x >= CART.x - 2 && x <= CART.x + 50 && a < 0.55) return 197 + Math.round(Math.abs(x - CART.x - 24) / 26 * 7);   // on the umbrella
  if (a < 0.14) return EMB;
  return PAVE + 2 + Math.floor(b * (H - PAVE - 4));
}
const RAIN = [
  { count: 190, nMin: 160, nMax: 190, fall: 200, ff: 1, len: 2, slope: 0.1, col: 'rgba(140,150,186,0.3)', warm: 'rgba(255,206,140,0.48)', splash: '#8a92b8',
    land: (x, a) => 150 + Math.floor(a * (WL - 150)) },
  { count: 130, nMin: 200, nMax: 230, fall: 250, ff: 0.9, len: 3, slope: 0.14, col: 'rgba(160,172,206,0.38)', warm: 'rgba(255,210,150,0.62)', splash: '#a4acd0',
    land: (x, a) => WL + 2 + Math.floor(a * (EMB - WL - 3)) },
  { count: 60, nMin: 250, nMax: 280, fall: 300, ff: 0.84, len: 6, slope: 0.18, col: 'rgba(196,206,232,0.5)', warm: 'rgba(255,218,160,0.78)', splash: '#c4cce8',
    land: nearLand },
];
RAIN.forEach((L, li) => {
  const r = rng(300 + li);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
const RINGS = [], PUDDLE_RINGS = [];
{ const r = rng(81); for (let i = 0; i < 46; i++) RINGS.push({ id: i, n: 100 + Math.floor(r() * 40), ph: r() }); }
{ const r = rng(82); for (let i = 0; i < 22; i++) PUDDLE_RINGS.push({ id: 60 + i, n: 80 + Math.floor(r() * 30), ph: r() }); }
const PUDDLES = [[104, 246, 16, 2], [214, 241, 22, 2], [286, 256, 18, 3], [168, 263, 24, 3], [360, 250, 14, 2]];

/* ================= Pre-render everything static ================= */
const CAR_LANES = [];
{ const r = rng(91); for (let i = 0; i < 7; i++) CAR_LANES.push({ id: i, k: 5 + i, ph: r(), dir: i & 1 ? -1 : 1, col: Math.floor(r() * CAR_COLS.length) }); }
let HL, SKY_C, CLOUDS, SCUD, FAR, HAZE_C, WILLIS, MID, MARINA, BILL_C, POSTERS, PITCH, BR_BACK, BR_FRONT, TENDER_C, TRAIN_C,
  CARS_C, TAXI_C, TOUR_C, WATER_C, GROUND_C, LAMPS_C, CART_C, CAFE_C, GULL_C, GULL_SIT_C, FOG, VIG_C, CROWN_C, TREE_C;
function prerender() {
  HL = {
    red: makeHalo(5, '#ff3a30', 0.7, 1), redWide: makeHalo(12, '#ff4a40', 0.22, 1.4), crown: makeHalo(26, '#d8e4ff', 0.2, 1.5),
    flash: makeHalo(96, '#aab4e4', 0.3, 1.6), skydeck: makeHalo(9, '#ffe0a8', 0.22, 1.4),
    lamp: makeHalo(56, '#ffb866', 0.26, 1.7), lampCore: makeHalo(16, '#ffe6b0', 0.5, 1.4),
    farLamp: makeHalo(7, '#ffcf88', 0.42, 1.2), pierLamp: makeHalo(9, '#ffd890', 0.45, 1.2),
    bulb: makeHalo(8, '#ffd08a', 0.45), cart: makeHalo(32, '#ffb060', 0.2, 1.6), cafe: makeHalo(46, '#ffac58', 0.22, 1.6),
    tender: makeHalo(18, '#ffc070', 0.28, 1.5), trainWin: makeHalo(11, '#ffe0a8', 0.22, 1.4), spark: makeHalo(7, '#c4e4ff', 0.7, 1.1),
    head: makeHalo(8, '#fff4d0', 0.5, 1.2), tail: makeHalo(4, '#ff3a30', 0.5, 1), billGlow: makeHalo(36, '#ffc878', 0.16, 1.6),
    billLamp: makeHalo(6, '#ffe0a0', 0.5, 1.3), sigR: makeHalo(4, '#ff3a30', 0.6, 1), sigG: makeHalo(4, '#40ff90', 0.6, 1),
    boat: makeHalo(16, '#ffc27a', 0.22), mast: makeHalo(4, '#ffffff', 0.5, 1), phone: makeHalo(5, '#9ad8ff', 0.4, 1.2),
    strobe: makeHalo(5, '#ffffff', 0.7, 1), win: makeHalo(10, '#ffb45a', 0.14),
  };
  SKY_C = renderSky();
  CLOUDS = CLOUD_DEFS.map(makeCloudLayer); SCUD = makeCloudLayer(SCUD_DEF);
  FAR = renderFar(); FAR.c = trim(FAR.c); HAZE_C = trim(renderHaze());
  WILLIS = renderWillis(); WILLIS.c = trim(WILLIS.c);
  MID = renderMid(); MID.c = trim(MID.c);
  MARINA = renderMarina(); MARINA.c = trim(MARINA.c);
  BILL_C = trim(renderBillboard()); POSTERS = [renderPoster(false), renderPoster(true)];
  PITCH = makePitch(BILL.w, BILL.h, { accent: '#b3ddf2' });
  BR_BACK = trim(renderBridgeBack()); BR_FRONT = trim(renderBridgeFront()); TENDER_C = trim(renderTender());
  const tr = renderTrain(); TRAIN_C = [tr, flipH(tr)];
  CARS_C = renderCars(); TAXI_C = renderTaxi(); TOUR_C = renderTour();
  WATER_C = renderWater(); GROUND_C = trim(renderGround()); LAMPS_C = trim(renderLamps()); CART_C = trim(renderCart()); CAFE_C = trim(renderCafe());
  CROWN_C = renderTreeCrown(); TREE_C = trim(renderTree());
  GULL_C = GULL_ROWS.map(rows => [sprite(rows, GULL_KEY), sprite(rows, GULL_KEY, true)]);
  GULL_SIT_C = GULL_SIT.map(rows => sprite(rows, GULL_KEY));
  FOG = makeFog(FOG_DEF); VIG_C = renderVignette();
}

/* ================= Per-frame: sky, far flash, the airliner ================= */
function drawScroll(cv, y, k, t, a = 1) {
  const off = scroll(t, k, W);
  ctx.globalAlpha = a; ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); ctx.globalAlpha = 1;
}
function flashLevel(t) {
  for (const f of FLASHES) {
    const d = t - f.t;
    if (d >= 0 && d < 1.8) return { x: f.x, a: Math.sin(Math.PI * d / 1.8) ** 2 * (0.75 + 0.25 * Math.sin(d * 9)) };
  }
  return null;
}
function drawPlane(t) {
  if (t < PLANE.a || t >= PLANE.b) return;
  const u = (t - PLANE.a) / (PLANE.b - PLANE.a), x = Math.round(lerp(490, -12, u)), y = Math.round(36 + u * 30);
  R(x, y, 4, 1, '#20222e'); px(ctx, x + 2, y - 1, '#20222e');
  halo(HL.head, x - 1, y, 0.45); px(ctx, x - 1, y, '#fff8e8');
  if (frac(t / 1.2) < 0.1) { px(ctx, x + 2, y, '#ffffff'); halo(HL.strobe, x + 2, y, 0.7); }
  if (frac(t / 1.5 + 0.3) < 0.5) px(ctx, x + 3, y - 1, '#ff3a3a');
}
function drawSky(t, storm) {
  ctx.drawImage(SKY_C, 0, 0);
  drawScroll(CLOUDS[0].c, CLOUDS[0].y, CLOUDS[0].k, t);
  drawPlane(t);
  drawScroll(CLOUDS[1].c, CLOUDS[1].y, CLOUDS[1].k, t);
  const f = storm ? flashLevel(t) : null;                            // the flash lights the clouds from inside, far off
  if (f) halo(HL.flash, f.x, 66, f.a);
  drawScroll(CLOUDS[2].c, CLOUDS[2].y, CLOUDS[2].k, t);
  if (f) halo(HL.flash, f.x, 96, f.a * 0.35);
}

/* ================= Per-frame: the far Loop, Willis Tower, the scud ================= */
const winOn = (w, t, salt) => hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p;
function drawFar(t) {
  blit(FAR.c);
  ctx.fillStyle = '#a88a5e';
  for (const w of FAR.wins) if (winOn(w, t, 0)) { ctx.fillStyle = w.col; ctx.fillRect(w.x, w.y, 1, 1); }
  blit(HAZE_C, 0.26);
}
function drawWillis(t) {
  blit(WILLIS.c);
  for (const w of WILLIS.wins) if (winOn(w, t, 500)) px(ctx, w.x, w.y, w.col);
  // a cleaning crew works its way up, a floor at a time
  const s = step(t, 20), f = 22 + s * 4, y = fy(f) & ~1, fade = smooth(0, 1.5, t - s * 12);
  if (!WT_BANDS.some(b => Math.abs(b - y) <= 1)) {
    ctx.globalAlpha = 0.85 * fade; ctx.fillStyle = '#f2eedc';
    for (const col of WT_COLS) if (!col.side && y > col.top + 1) for (let i = col.x + 1; i < col.x + col.w; i += 2) ctx.fillRect(i, y, 1, 1);
    ctx.globalAlpha = 1;
  }
  halo(HL.skydeck, 199, fy(103), 0.8 + 0.2 * wave(t, 6));
  const u = frac(t / 1.5), a = smooth(0, 0.12, u) * (1 - smooth(0.4, 0.56, u));   // the antenna lamps, together
  for (const an of ANT) for (const ly of [an.tip, WT.roof - 16]) {
    px(ctx, an.x, ly, '#5a1c1a');
    if (a > 0.02) { ctx.globalAlpha = a; px(ctx, an.x, ly, '#ff5040'); ctx.globalAlpha = 1; halo(HL.red, an.x, ly, a * 0.9); }
  }
  for (const an of ANT) halo(HL.redWide, an.x, an.tip, a * 0.6);
}
function drawScud(t) {
  drawScroll(SCUD.c, SCUD.y, SCUD.k, t, 0.32);
  const v = env(t, VEIL.a, VEIL.b, VEIL.f);
  if (v > 0.01) {                                                    // a thicker veil drifts over the top, then clears
    const off = Math.round(lerp(-W * 0.6, W * 0.35, (t - VEIL.a) / (VEIL.b - VEIL.a)));
    ctx.globalAlpha = 0.5 * v; ctx.drawImage(SCUD.c, 0, 0, W, SCUD_DEF.h, off, SCUD.y + 10, W, SCUD_DEF.h); ctx.globalAlpha = 1;
  }
}

/* ================= Per-frame: the river-front towers, Marina City, the billboard ================= */
function drawMid(t) {
  halo(HL.crown, CROWN.x + 9, CROWN.top + 3, 0.85 + 0.15 * wave(t, 5));
  blit(MID.c);
  for (const w of MID.wins) if (winOn(w, t, 900)) px(ctx, w.x, w.y, w.col);
  ctx.globalAlpha = 0.5 + 0.2 * wave(t, 12, 0.3);                     // a slow shimmer of light across the curved glass
  const sx = 62 + Math.round(frac(t * 3 / LOOP) * 70);
  for (let y = 112; y < 146; y += 3) R(sx + ((y >> 1) & 3), y + 1, 3, 1, '#6aa898');
  ctx.globalAlpha = 1;
}
function drawMarina(t) {
  blit(MARINA.c);
  for (const w of MARINA.wins) if (winOn(w, t, 1300)) px(ctx, w.x, w.y, w.col);
  const u = frac(t / 2 + 0.25), a = smooth(0, 0.1, u) * (1 - smooth(0.35, 0.5, u));
  for (const cx of MC) { px(ctx, cx + 1, MC_TOP - 5, a > 0.3 ? '#ff4a3a' : '#5a1c1a'); halo(HL.red, cx + 1, MC_TOP - 5, a * 0.8); }
  const v = frac(t / 24);                                             // a car climbs the garage's spiral, headlights first
  if (v < 0.4) {
    const s = v / 0.4, cx = MC[step(t, 10) & 1], x = Math.round(cx - 8 + 16 * s), y = MC_PARK + 8 - Math.round(s * 3);
    px(ctx, x, y, '#fff0c0'); px(ctx, x - 3, y, '#ff3a30'); halo(HL.head, x + 1, y, 0.5);
  }
}
const STEAM = [[4, 0, '#c8c0b8'], [8, 0.45, '#d8d0c8']];
function drawPoster(t, lv) {
  ctx.drawImage(POSTERS[0], BILL.x, BILL.y);
  if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(POSTERS[1], BILL.x, BILL.y); ctx.globalAlpha = 1; }
  for (const [ox, ph, col] of STEAM) for (let j = 1; j <= 5; j++) {   // steam curling off the slice
    const y = BILL.y + 8 - j, sway = Math.sin(y * 1.2 + TAU * (72 * t / LOOP + ph)) * Math.min(1.5, j * 0.4);
    ctx.globalAlpha = 1 - j * 0.15; px(ctx, BILL.x + 1 + ox + Math.round(sway), y, col);
  }
  ctx.globalAlpha = 1;
}
function drawBillboard(t, pitch) {
  const lv = flick(t, BILL_FLICK, 13);
  halo(HL.billGlow, BILL.x + (BILL.w >> 1), BILL.y + 4, (0.9 + 0.1 * wave(t, 8, 0.2)) * (2 + lv) / 3);
  blit(BILL_C);
  if (!PITCH.covers(t, pitch)) drawPoster(t, lv);
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
  BILL_LAMPS.forEach((lx, i) => {
    const a = i === 2 ? lv : 1;
    if (a < 1) R(lx - 5, BILL_LAMP_Y + 1, 3, 2, rgbaC('#281a12', +(0.85 * (1 - a)).toFixed(2)));
    halo(HL.billLamp, lx - 4, BILL_LAMP_Y + 3, a * (0.92 + 0.08 * wave(t, 700 + i * 37)));
  });
}

/* ================= Per-frame: the bridge, its traffic, the L and the tender ================= */
function drawCars(t) {
  for (const c of CAR_LANES) {
    const u = frac(c.k * t / LOOP + c.ph), x = Math.round(c.dir > 0 ? -12 + u * (W + 24) : W + 12 - u * (W + 24)) - 5, y = BOTC - 5;
    if (x < -12 || x > W) continue;
    ctx.drawImage(CARS_C[c.col][c.dir > 0 ? 0 : 1], x, y);
    halo(HL.head, c.dir > 0 ? x + 11 : x - 1, y + 2, 0.5); halo(HL.tail, c.dir > 0 ? x - 1 : x + 11, y + 2, 0.5);
  }
}
function drawTrain(t, reducedMotion) {
  for (const tr of TRAINS) {
    if (t < tr.a || t >= tr.b) continue;
    const u = (t - tr.a) / (tr.b - tr.a), e = smooth(0, 0.12, u) * 0.4 + 0.6 * u;   // eases off the station, then runs
    const x = Math.round(tr.dir > 0 ? -LTRAIN + e * (W + LTRAIN) : W - e * (W + LTRAIN)), y = TRK - LH;
    ctx.drawImage(TRAIN_C[tr.dir > 0 ? 0 : 1], x, y);
    for (let k = 0; k < LCARS; k++) {
      const cx = x + k * (LCAR + 1) + (LCAR >> 1);
      halo(HL.trainWin, cx, y + 5, 0.75);
      for (const sh of [4, LCAR - 5]) {                                // sparks off the third rail, now and then
        const sxp = x + k * (LCAR + 1) + sh, hv = hash(k * 7 + sh, step(t, 2400), tr.a);
        if (hv < 0.05) { px(ctx, sxp, TRK, '#f0faff'); px(ctx, sxp + (hv < 0.025 ? 1 : -1), TRK + 1, '#9ad0ff'); if (!reducedMotion) halo(HL.spark, sxp, TRK, 0.8); }
      }
    }
    halo(HL.head, tr.dir > 0 ? x + LTRAIN : x - 1, y + 8, 0.9);
  }
}
function drawTender(t, crowd) {
  blit(TENDER_C);
  if (here(RANK.tender, crowd)) {                                    // the bridge tender at the right window, a mug now and then
    const wx = TH.x + 19, wy = TH.wall + 5, look = boatNear(t) ? 1 : 0, sip = frac(t / 30 + 0.2) < 0.16;
    R(wx + look, wy + 3, 3, 3, '#3a2616'); R(wx - 1, wy + 6, 5, 4, '#3a2616');
    if (sip) { R(wx + 3, wy + 4, 2, 2, '#d8d0c0'); R(wx + 3, wy + 6, 1, 2, '#3a2616'); }
  }
  halo(HL.tender, TH.x + 9, TH.wall + 10, 0.7); halo(HL.tender, TH.x + 21, TH.wall + 10, 0.8 + 0.06 * wave(t, 40));
}
function drawBridge(t, crowd, reducedMotion) {
  blit(BR_BACK);
  FAR_LAMPS.forEach((lx, i) => halo(HL.farLamp, lx, 170, 0.8 + 0.2 * wave(t, 300 + i * 17)));
  drawCars(t);
  blit(BR_FRONT);
  PIER_LAMPS.forEach((lx, i) => halo(HL.pierLamp, lx, 143, 0.85 + 0.15 * wave(t, 400 + i * 23)));
  const green = TRAINS.some(tr => t >= tr.a - 2 && t < tr.b);         // the signals clear for each train
  for (const sx of [BX0 + 10, BX1 - 10]) {
    px(ctx, sx, TRK - 9, green ? '#3a1414' : '#ff4a3a'); px(ctx, sx, TRK - 8, green ? '#40ff90' : '#143a24');
    halo(green ? HL.sigG : HL.sigR, sx, green ? TRK - 8 : TRK - 9, 0.8);
  }
  drawTrain(t, reducedMotion);
  drawTender(t, crowd);
}

/* ================= Per-frame: gulls ================= */
function drawGulls(t) {
  for (const g of GULLS_T) {
    if (t < g.a || t >= g.b) continue;
    const u = (t - g.a) / (g.b - g.a);
    for (let k = 0; k < 3; k++) {                                    // three, loosely together
      const uk = clamp(u * 1.15 - k * 0.07, 0, 1);
      if (uk <= 0 || uk >= 1) continue;
      const x = Math.round(g.dir > 0 ? lerp(-18, W + 4, uk) : lerp(W + 4, -18, uk)) + k * 9 * g.dir;
      const y = Math.round(g.y0 + k * 6 + g.dip * Math.sin(Math.PI * uk) + 2 * wave(t, 40, k * 0.3));
      const glide = hash(k, step(t, 480), 21) < 0.4, fr = glide ? 1 : Math.floor(t * 6 + k * 0.7) % 3;
      ctx.drawImage(GULL_C[fr][g.dir > 0 ? 0 : 1], x, y);
    }
  }
}
function drawPerched(t) {                                            // a gull on the bollard turns its head, hops round now and then
  const turn = hash(5, step(t, 160), 4) < 0.5 ? 0 : 1, hop = frac(t / 30 + 0.4) < 0.03 ? 1 : 0;
  ctx.drawImage(GULL_SIT_C[turn], BOLLARDS[1] - 1, EMB - 11 - hop);
}

/* ================= Per-frame: the river, its boats and mist ================= */
const STREAKS = [
  ...FAR_LAMPS.map((x, i) => ({ x, w: 3, col: '#ffcf88', a: 0.34, seed: 1 + i })),
  ...PIER_LAMPS.map((x, i) => ({ x, w: 3, col: '#ffd890', a: 0.3, seed: 12 + i })),
  { x: TH.x + 15, w: 7, col: '#ffc070', a: 0.3, seed: 20 }, { x: CROWN.x + 9, w: 5, col: '#dce6ff', a: 0.14, seed: 21 },
  { x: 372, w: 5, col: '#f0bc70', a: 0.12, seed: 22 }, { x: 402, w: 5, col: '#f0bc70', a: 0.12, seed: 23 },
  { x: 199, w: 3, col: '#ff5040', a: 0.1, seed: 24 },
];
const STREAK_BB = { x: BILL.x + 26, w: 7, col: '#ffc878', a: 0.2, seed: 30 };
function drawRings(list, t, x0, x1, y0, y1, maxR, warmAt) {
  for (const g of list) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.45) continue;
    const s = u / 0.45, x = Math.floor(x0 + hash(g.id, cyc, 1) * (x1 - x0)), y = Math.floor(y0 + hash(g.id, cyc, 2) * (y1 - y0));
    const rr = 1 + s * maxR, ry = rr * 0.36, pts = 6 + Math.floor(rr * 2.5);
    ctx.fillStyle = warmAt(x, y) ? '#ffcf80' : '#8e98c0';
    ctx.globalAlpha = (1 - s) * 0.5;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
const nearStreak = x => STREAKS.some(st => Math.abs(st.x - x) < st.w + 2);
function drawRiver(t, rain, billboard) {
  ctx.drawImage(WATER_C, 0, WL);
  const rows = EMB - WL;
  reflect(ctx, t, { top: WL, rows, alpha: 0.5, squash: 1.7, amp: 1, grow: 0.07 });
  R(0, WL, W, rows, 'rgba(10,16,24,0.24)');
  R(0, WL, W, 1, '#0c0e14');
  for (let y = WL + 2, n = 0; y < EMB; y += 2, n++) for (let j = 0; j < 4; j++) {   // drifting ripple troughs and glints
    const k = 3 + ((n * 7 + j * 5) % 9), len = 5 + Math.floor(hash(n, j, 41) * 16);
    const x = Math.round(frac(hash(n, j, 40) - k * t / LOOP) * (W + 40)) - 20;
    ctx.globalAlpha = j === 3 ? 0.2 : 0.4;
    R(x, y + (j & 1), len, 1, j === 3 ? '#7a8aa4' : '#070a10');
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, EMB);
  if (billboard) streak(ctx, t, STREAK_BB.x, STREAK_BB.w, STREAK_BB.col, STREAK_BB.a, STREAK_BB.seed, WL + 1, EMB);
  if (trainOn(t)) for (const tr of TRAINS) if (t >= tr.a && t < tr.b) {   // the L's windows laid down the water
    const u = (t - tr.a) / (tr.b - tr.a), e = smooth(0, 0.12, u) * 0.4 + 0.6 * u;
    const x = tr.dir > 0 ? -LTRAIN + e * (W + LTRAIN) : W - e * (W + LTRAIN);
    for (let k = 0; k < LCARS; k++) streak(ctx, t, x + k * (LCAR + 1) + 13, 14, '#ffe0a8', 0.16, 40 + k, WL + 6, EMB);
  }
  if (rain) drawRings(RINGS, t, 0, W, WL + 3, EMB - 3, 5, nearStreak);
}
function boatMirror(spr, x, y) {
  const w = spr.width, h = spr.height;
  for (let j = 0; j < 9; j++) {
    ctx.globalAlpha = 0.28 * (1 - j / 9);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + Math.round(Math.sin(j * 1.3) * 0.7), y + h + j, w, 1);
  }
  ctx.globalAlpha = 1;
}
function wake(t, sx, y, dir, n) {
  for (let k = 0; k < n; k++) {
    const wx = sx - dir * (k * 2 + (k * k >> 3)), wy = y + ((k * 7) % 3) - (k >> 3);
    if (hash(k, step(t, 1440), 17) < 0.65 - k * 0.025) px(ctx, wx, wy, k < 5 ? '#c8ccdc' : '#6a7490');
  }
}
function drawBoats(t) {
  if (t >= TAXI.a && t < TAXI.b) {                                   // the water taxi, up the river from the lake
    const u = (t - TAXI.a) / (TAXI.b - TAXI.a), x = Math.round(lerp(-40, W + 4, u)), y = 194 + Math.round(wave(t, 120) * 0.6);
    boatMirror(TAXI_C, x, y);
    wake(t, x + 2, y + 12, 1, 16);
    ctx.drawImage(TAXI_C, x, y);
    halo(HL.boat, x + 18, y + 5, 0.75); halo(HL.mast, x + 14, y, 0.8); halo(HL.red, x + 36, y + 9, 0.5);
    streak(ctx, t, x + 18, 5, '#ffd07a', 0.3, 7, y + 14, EMB);
  }
  if (t >= TOUR.a && t < TOUR.b) {                                   // the sightseeing boat, down from the bend
    const u = (t - TOUR.a) / (TOUR.b - TOUR.a), x = Math.round(lerp(W + 4, -76, u)), y = 199 + Math.round(wave(t, 100, 0.3) * 0.6);
    boatMirror(TOUR_C, x, y);
    wake(t, x + 70, y + 14, -1, 22);
    ctx.drawImage(TOUR_C, x, y);
    for (const ox of [16, 34, 52]) halo(HL.boat, x + ox, y + 11, 0.6);
    halo(HL.mast, x + 66, y, 0.8); halo(HL.sigG, x + 2, y + 10, 0.6);
    streak(ctx, t, x + 36, 16, '#ffd07a', 0.22, 8, y + 16, EMB);
  }
}
function drawFog(t) {
  const D = clamp(FOG.dens(t), 0, 0.999) * FOG_LEVELS, k = Math.floor(D), fr = D - k, off = scroll(t, FOG.k, W);
  for (const [cv, a] of [[FOG.levels[k], 1 - fr], [FOG.levels[k + 1], fr]]) {
    if (a * FOG.alpha < 0.004) continue;
    ctx.globalAlpha = a * FOG.alpha;
    ctx.drawImage(cv, off - W, FOG.y); ctx.drawImage(cv, off, FOG.y);
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: rain ================= */
function drawRain(L, t, lights) {
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -24 + hash(d.id, cyc, 11) * (W + 24 + L.fall * L.slope);
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12), hash(d.id, cyc, 13));
    if (u < L.ff) {
      const s = u / L.ff, yh = yl - L.fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1) continue;
      ctx.fillStyle = litAt(xh, yh, lights) ? L.warm : L.col;
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh - k * L.slope), Math.round(yh - k), 1, 1);
    } else {
      const s = (u - L.ff) / (1 - L.ff), big = L.len > 4;
      const hgt = Math.sin(Math.PI * s) * (big ? 2.5 : 1.2), spread = 1 + s * (big ? 2.5 : 1.2);
      ctx.globalAlpha = 1 - s;
      ctx.fillStyle = litAt(xl, yl, lights) ? L.warm : L.splash;
      const Y = Math.round(yl - 1 - hgt);
      ctx.fillRect(Math.round(xl - spread), Y, 1, 1); ctx.fillRect(Math.round(xl + spread), Y, 1, 1);
      if (s < 0.35) ctx.fillRect(Math.round(xl), Math.round(yl - 1), 1, 1);
      ctx.globalAlpha = 1;
    }
  }
}

/* ================= Per-frame: the Riverwalk and its people ================= */
const mirror = (x, fy, t) => reflect(ctx, t, { top: Math.round(fy) + 1, rows: 8, x: Math.round(x) - 10, w: 21, squash: 1.4, alpha: 0.26, k1: 40, k2: 90 });
function steamPuffs(x, y, t, n, id, col = '#c8ccd8', rise = 12) {
  for (let k = 0; k < n; k++) {
    const { u } = cycle(t, 50 + k * 10, k * 0.29 + id * 0.13);
    ctx.globalAlpha = 0.5 * Math.sin(Math.PI * u);
    R(Math.round(x + k * 3 + Math.sin(u * 5 + k) * 1.5), Math.round(y - u * rise), 1 + (u > 0.5 ? 1 : 0), 1, col);
  }
  ctx.globalAlpha = 1;
}
function drawFarRow(t, crowd) {                                       // at the river's edge, looking out
  if (here(RANK.tourists, crowd)) {                                   // two visitors share an umbrella; one points out the tower
    const point = frac(t / 24 + 0.6) < 0.35;
    person(178, 232, CAST.tourA, { view: 0, aR: point ? 1 : 0, lean: point ? 0 : 1 });
    person(187, 232, CAST.tourB, { view: 0, aL: 1 });
    umbrella(183, PH.hy - 7, 9, ...UMB.tourists, PH.ty + 1);
    mirror(182, 232, t);
  }
  if (here(RANK.photo, crowd)) {                                      // a photographer: Willis Tower in the rain, and the L
    const up = frac(t / 20 + 0.1) < 0.35 || trainOn(t);
    person(300, 233, CAST.photo, { view: 0, aL: 1, aR: up ? 2 : 0 });
    if (up) phoneBack(PH.tx + PH.bw, PH.hy - 1);
    umbrella(PH.tx - 1, PH.hy - 7, 8, ...UMB.photo, PH.ty);
    if (up) halo(HL.phone, PH.tx + PH.bw + 1, PH.hy, 0.6);
    mirror(300, 233, t);
  }
}
function drawCouple(t, crowd) {
  if (!here(RANK.couple, crowd)) return;
  const u = frac(t / 40 + 0.3), close = u > 0.5 && u < 0.8, point = boatNear(t) && frac(t / 10) < 0.5;
  person(147, 240, CAST.her, { view: 0, aR: point ? 3 : 0, lean: close ? -1 : 0 });
  person(139, 240, CAST.him, { view: 0, aR: 1, lean: close ? 1 : 0 });
  umbrella(143, PH.hy - 6, 10, ...UMB.couple, PH.ty + 1);
  mirror(143, 240, t);
}
function drawStand(t, crowd) {
  const q = (t - SERVE.from) / SERVE.every, s = frac(q) * SERVE.every, who = ((Math.floor(q) % 6) + 6) % 6;
  if (here(RANK.vendor, crowd)) {                                     // the vendor at the counter: tongs, then a dog handed over
    const hand = s > 13 && s < 17 && here(RANK.customer, crowd);
    person(73, 231, CAST.vendor, { view: 1, aR: hand ? 3 : 0, aL: !hand && (step(t, 160) & 1) ? 2 : 0 });
  }
  blit(CART_C);
  steamPuffs(CART.x + 8, CART.top - 6, t, 4, 1);
  halo(HL.cart, CART.x + 24, 214, 0.9); halo(HL.bulb, CART.x + 24, 207, 1);
  if (!here(RANK.customer, crowd) || s >= 30) return;
  const P = CAST.customers[who % 3];
  let x, o;
  if (s < 10) { x = lerp(-12, 104, s / 10); o = { view: 2, dir: 1, step: walkF(t) }; }
  else if (s < 20) { x = 104; o = { view: 2, dir: -1, aR: s > 14 && s < 17 ? 3 : 0 }; }
  else { x = lerp(104, -12, (s - 20) / 10); o = { view: 2, dir: -1, step: walkF(t), aR: frac(s / 3) < 0.4 ? 2 : 3 }; }
  person(x, 252, P, o);
  if (s >= 17 && s < 20) { R(PH.tx - 3, PH.ty + 1, 3, 1, '#e8c060'); R(PH.tx - 3, PH.ty, 3, 1, '#c86a3a'); }   // the dog in its bun
  else if (s >= 20) { const hx = o.aR === 2 ? Math.round(x) - 4 : Math.round(x) - 6, hy = o.aR === 2 ? PH.hy + 1 : PH.ty + 1; R(hx, hy, 3, 1, '#e8c060'); }
  mirror(x, 252, t);
}
function drawCafe(t, crowd) {
  blit(CAFE_C);
  for (const bx of [CAFE.ax + 13, CAFE.ax + 26, CAFE.ax + 39]) {      // bulbs hanging inside the vault, swaying a little in the draught
    const sw = Math.round(0.6 * wave(t, 20, bx * 0.01));
    R(bx + sw, CAFE.top + 6, 1, 6, '#2a1a12'); px(ctx, bx + sw, CAFE.top + 12, '#fff0c0');
    halo(HL.bulb, bx + sw, CAFE.top + 12, 0.9);
  }
  if (here(RANK.cafe, crowd)) {                                       // the barman steps out under the girder to watch the rain
    const sip = frac(t / 30 + 0.55) < 0.2;
    person(412, 246, CAST.cafe, { view: 1, aR: sip ? 2 : 0 });
    if (sip) { R(PH.tx + PH.bw, PH.hy + 2, 2, 2, '#e8e2d8'); steamPuffs(PH.tx + PH.bw, PH.hy + 1, t, 2, 3, '#d8d4cc', 6); }
    mirror(412, 246, t);
  }
  halo(HL.cafe, CAFE.ax + 26, 214, 0.95);
  const glow = 0.7 + 0.08 * wave(t, 16, 0.2);
  halo(HL.win, CAFE.ax + 26, 168, glow);
  ctx.globalAlpha = 0.3 + 0.3 * wave(t, 30, 0.1);                     // the sign's bulbs breathe
  text(ctx, 'CAFE', Math.round(CAFE.ax + CAFE.aw / 2 - textWidth('CAFE') / 2), 166, '#fff6dc');
  ctx.globalAlpha = 1;
}
function drawWalkers(t, crowd) {
  let u;
  if (here(RANK.walkA, crowd) && (u = inWin(t, [WALK_A])) >= 0) {     // home along the river under an umbrella
    const x = lerp(W + 16, -16, u);
    person(x, 256, CAST.walkA, { view: 2, dir: -1, step: walkF(t), aR: 1 });
    umbrella(Math.round(x) - 2, PH.hy - 7, 10, ...UMB.walkA, PH.ty);
    mirror(x, 256, t);
  }
  if (here(RANK.walkB, crowd) && (u = inWin(t, [WALK_B])) >= 0) {     // with a tote from the market, the other way
    const x = lerp(-16, W + 16, u);
    person(x, 260, CAST.walkB, { view: 2, dir: 1, step: walkF(t), aR: 1 });
    R(Math.round(x) - 4, PH.ty + 6, 3, 5, '#d8c8a0'); R(Math.round(x) - 3, PH.ty + 4, 1, 2, '#8a7a5a');
    umbrella(Math.round(x) + 2, PH.hy - 7, 10, ...UMB.walkB, PH.ty);
    mirror(x, 260, t);
  }
  if (here(RANK.jog, crowd) && (u = inWin(t, JOG)) >= 0) {            // a jogger in a hood, rain or not
    const x = lerp(-20, W + 20, u), st = Math.floor(t * 6.4) & 1, fy = 264 - st;
    person(x, fy, CAST.jog, { view: 2, dir: 1, step: st, aR: st ? 2 : 3 });
    mirror(x, 264, t);
  }
}
const PAVE_STREAKS = [{ x: LAMPS[0].x, w: 5, col: '#ffc878', a: 0.34, seed: 51 }, { x: LAMPS[1].x, w: 5, col: '#ffc878', a: 0.34, seed: 52 },
  { x: CART.x + 24, w: 7, col: '#ffb060', a: 0.2, seed: 53 }, { x: CAFE.ax + 26, w: 12, col: '#ffac58', a: 0.26, seed: 54 }];
const TREE_LEAVES = [0, 1, 2, 3, 4].map(i => ({ id: i, n: 10 + i * 2, ph: i * 0.23 }));
function drawTree(t, own) {
  blit(TREE_C);
  const w = CROWN_C.width, h = CROWN_C.height, x0 = TREE.x - (w >> 1), y0 = TREE.cy - (h >> 1);
  for (let j = 0; j < h; j++) {                                      // the crown sways, more at the top
    const off = Math.round((1 - j / h) * (1.3 * wave(t, 16, 0.1) + 0.6 * wave(t, 41, 0.3)));
    ctx.drawImage(CROWN_C, 0, j, w, 1, x0 + off, y0 + j, w, 1);
  }
  if (!own) return;
  for (const l of TREE_LEAVES) {                                     // now and then a leaf lets go and spins down to the stone
    const { cyc, u } = cycle(t, l.n, l.ph);
    if (u > 0.5) continue;
    const s = u / 0.5, sx = TREE.x - 12 + hash(l.id, cyc, 2) * 24, y = TREE.cy + 4 + s * (240 + hash(l.id, cyc, 3) * 20 - TREE.cy - 4);
    const x = sx + 3 * Math.sin(s * 9 + l.id) + s * 8;
    ctx.globalAlpha = s > 0.9 ? (1 - s) * 10 : 1;
    px(ctx, Math.round(x), Math.round(y), Math.sin(s * 23 + l.id) > 0 ? '#e0b840' : '#9a7a26');
  }
  ctx.globalAlpha = 1;
}
function drawForeground(t, rain, crowd, own) {
  blit(GROUND_C);
  reflect(ctx, t, { top: PAVE, rows: 18, squash: 2.4, alpha: 0.2, k1: 40, k2: 90, amp: 0.6 });   // the wet stone holds the river's light
  PUDDLES.forEach(([cx, cy, rx], pi) => {                               // the puddles shiver
    ctx.fillStyle = Math.abs(cx - LAMPS[0].x) < 40 || Math.abs(cx - LAMPS[1].x) < 40 ? '#c8a070' : '#5e6a86';
    for (let k = 0; k < 4; k++) {
      const { cyc, u } = cycle(t, 40 + ((pi * 7 + k * 13) % 30), k * 0.21 + pi * 0.13);
      ctx.globalAlpha = Math.sin(Math.PI * u) * 0.7;
      ctx.fillRect(Math.round(cx + (hash(pi, k, cyc) * 1.6 - 0.8) * rx) - 1, cy + (k & 1), 3, 1);
    }
  });
  ctx.globalAlpha = 1;
  for (const s of PAVE_STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, 238, H);
  if (rain) drawRings(PUDDLE_RINGS, t, 10, 400, PAVE + 6, H - 4, 3, x => Math.abs(x - LAMPS[0].x) < 20 || Math.abs(x - LAMPS[1].x) < 20);
  drawPerched(t);
  if (crowd > 0) drawFarRow(t, crowd);
  drawTree(t, own);
  blit(LAMPS_C);
  drawCouple(t, crowd);
  drawStand(t, crowd);
  drawCafe(t, crowd);
  drawWalkers(t, crowd);
  LAMPS.forEach((L, i) => {
    const li = (0.95 + 0.05 * wave(t, 1500, 0.3 + i * 0.4)) * (i === 0 ? flick(t, LAMP_FLICK, 11) : 1);
    if (li < 0.9) R(L.x - 2, L.y - 2, 6, 7, rgbaC('#180e08', +(0.9 - li).toFixed(2)));
    halo(HL.lamp, L.x, L.y + 1, li); halo(HL.lampCore, L.x, L.y + 1, li);
  });
}

/* ================= Frame compositor (back to front) ================= */
// pitch: a face over the billboard (core makePitch); billboard: false leaves the sign out (the loop videos); weather: a
// WEATHERS pick (site/js/engine/weather.js), the city's own rain when left out; crowd: the share of the people to show,
// 0..1 (CROWD when left out or not a number), 0 = nobody; reducedMotion: no far flashes, no spark glints
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), own = wx === WEATHER('city');
  const lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS, wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t, own && !reducedMotion);
  drawFar(t);
  drawWillis(t);
  drawScud(t);
  if (wx.rain) drawRain(RAIN[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawMid(t);
  drawMarina(t);
  if (billboard) drawBillboard(t, pitch);
  drawBridge(t, crowd, reducedMotion);
  drawGulls(t);
  drawRiver(t, wx.rain, billboard);
  drawBoats(t);
  drawFog(t);
  if (wx.rain) drawRain(RAIN[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawForeground(t, wx.rain, crowd, own);
  if (wx.rain) drawRain(RAIN[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'chicago',
  name: 'Chicago',
  tagline: 'Rain on the Chicago River',
  timeZone: 'America/Chicago',
  accent: '#b3ddf2',                                   // the pale blue of the city flag
  focusX: 203,
  landmark: [184, 222],                                // Willis Tower, from the west ledge to the 50th-floor step
  landmarkTop: 22,                                     // the row of the east antenna's tip
  billboard: BILL,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { rain: 0.6, traffic: 0.3, water: 0.3, wind: 0.15 },
  events: [
    ...TRAINS.map(tr => ({ t: tr.a, type: 'train', dur: tr.b - tr.a, pan: tr.dir > 0 ? [-1, 1] : [1, -1], gain: 0.4 })),
    { t: TAXI.a, type: 'boat', dur: TAXI.b - TAXI.a, pan: [-1, 0.9], gain: 0.25 }, { t: TAXI.horn, type: 'horn', x: 30, gain: 0.35 },
    { t: 52, type: 'gull', x: 200, gain: 0.35 },
    ...FLASHES.map(f => ({ t: +(f.t + 1.2).toFixed(1), type: 'thunder', x: f.x, gain: 0.22 })),
    { t: TOUR.a, type: 'boat', dur: TOUR.b - TOUR.a, pan: [1, -1], gain: 0.3 }, { t: TOUR.horn, type: 'horn', x: 450, gain: 0.4 },
    { t: 162, type: 'gull', x: 300, gain: 0.35 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
